/**
 * main.js -- application entry point.
 *
 * Owns the emulator loop and wires the pieces together:
 *   core.js     the wasm emulator (or the stub)
 *   audio.js    Web Audio output pulled from the core's ring buffer
 *   input.js    keyboard + Gamepad -> libretro joypad state
 *   storage.js  IndexedDB for ROMs and save states
 */

import { loadCore } from './core.js';
import { AudioOutput } from './audio.js';
import {
  InputManager,
  REMAPPABLE_BUTTONS,
  ACTIONS,
  MODIFIABLE_ACTIONS,
  describeBinding,
  JOYPAD,
  JOYPAD_ID_TO_NAME,
  parseExtraKey,
} from './input.js';
import { GLFilterRenderer, ADVANCED_FILTERS } from './webgl/gl-filters.js';
import { TouchControls } from './touch-controls.js';
import { isValidCheatCode, normalizeCheatCode, applyCheats } from './cheats.js';
import { detectEditor } from './game-editors/index.js';
import * as storage from './storage.js';
import * as sync from './sync.js';

/* ------------------------------------------------------------------ DOM -- */

const el = {
  canvas: document.getElementById('screen'),
  stage: document.getElementById('stage'),
  dropHint: document.getElementById('drop-hint'),
  toast: document.getElementById('toast'),
  pauseOverlay: document.getElementById('pause-overlay'),
  coreBadge: document.getElementById('core-badge'),
  fpsBadge: document.getElementById('fps-badge'),
  romInput: document.getElementById('rom-input'),
  btnPause: document.getElementById('btn-pause'),
  btnReset: document.getElementById('btn-reset'),
  btnMute: document.getElementById('btn-mute'),
  btnFullscreen: document.getElementById('btn-fullscreen'),
  filterSelect: document.getElementById('filter-select'),
  scanlinesToggle: document.getElementById('scanlines-toggle'),
  ffAudioToggle: document.getElementById('ff-audio-toggle'),
  advancedVideoToggle: document.getElementById('advanced-video-toggle'),
  advancedFilterPicker: document.getElementById('advanced-filter-picker'),
  advancedFilterSelect: document.getElementById('advanced-filter-select'),
  touchControlsToggle: document.getElementById('touch-controls-toggle'),
  btnTouchEdit: document.getElementById('btn-touch-edit'),
  btnTouchReset: document.getElementById('btn-touch-reset'),
  canvasGl: document.getElementById('screen-gl'),
  syncPanel: document.getElementById('sync-panel'),
  syncConfig: document.getElementById('sync-config'),
  syncConfigCurrent: document.getElementById('sync-config-current'),
  syncConfigForm: document.getElementById('sync-config-form'),
  syncConfigUrl: document.getElementById('sync-config-url'),
  syncConfigKey: document.getElementById('sync-config-key'),
  btnSyncConfigClear: document.getElementById('btn-sync-config-clear'),
  syncSigninForm: document.getElementById('sync-signin-form'),
  syncEmail: document.getElementById('sync-email'),
  syncSignedIn: document.getElementById('sync-signed-in'),
  syncUserEmail: document.getElementById('sync-user-email'),
  btnSyncNow: document.getElementById('btn-sync-now'),
  btnSyncSignout: document.getElementById('btn-sync-signout'),
  btnForcePull: document.getElementById('btn-force-pull'),
  syncStatus: document.getElementById('sync-status'),
  btnSaveState: document.getElementById('btn-save-state'),
  btnLoadState: document.getElementById('btn-load-state'),
  slotSelect: document.getElementById('slot-select'),
  romList: document.getElementById('rom-list'),
  saveList: document.getElementById('save-list'),
  saveTotalSize: document.getElementById('save-total-size'),
  saveImportInput: document.getElementById('save-import-input'),
  btnSaveDownloadAll: document.getElementById('btn-save-download-all'),
  btnSaveDeleteAll: document.getElementById('btn-save-delete-all'),
  deleteAllSavesDialog: document.getElementById('delete-all-saves-dialog'),
  deleteAllSavesMessage: document.getElementById('delete-all-saves-message'),
  deleteAllSavesConfirmCheckbox: document.getElementById('delete-all-saves-confirm-checkbox'),
  btnDeleteAllSavesCancel: document.getElementById('btn-delete-all-saves-cancel'),
  btnDeleteAllSavesConfirm: document.getElementById('btn-delete-all-saves-confirm'),
  importConflictDialog: document.getElementById('import-conflict-dialog'),
  importConflictMessage: document.getElementById('import-conflict-message'),
  btnImportCancel: document.getElementById('btn-import-cancel'),
  btnImportAppend: document.getElementById('btn-import-append'),
  btnImportOverwrite: document.getElementById('btn-import-overwrite'),
  cheatList: document.getElementById('cheat-list'),
  gameEditorPanel: document.getElementById('game-editor-panel'),
  gameEditorGameName: document.getElementById('game-editor-game-name'),
  gameEditorHead: document.getElementById('game-editor-head'),
  gameEditorBody: document.getElementById('game-editor-body'),
  gameEditorEquipmentTitle: document.getElementById('game-editor-equipment-title'),
  gameEditorEquipmentHead: document.getElementById('game-editor-equipment-head'),
  gameEditorEquipmentBody: document.getElementById('game-editor-equipment-body'),
  gameEditorEsperTitle: document.getElementById('game-editor-esper-title'),
  gameEditorEsperHead: document.getElementById('game-editor-esper-head'),
  gameEditorEsperBody: document.getElementById('game-editor-esper-body'),
  gameEditorAilmentsTitle: document.getElementById('game-editor-ailments-title'),
  gameEditorAilmentsList: document.getElementById('game-editor-ailments-list'),
  gameEditorSpellsTitle: document.getElementById('game-editor-spells-title'),
  gameEditorSpellsList: document.getElementById('game-editor-spells-list'),
  gameEditorAbilitiesTitle: document.getElementById('game-editor-abilities-title'),
  gameEditorAbilitiesList: document.getElementById('game-editor-abilities-list'),
  gameEditorEsperInventoryTitle: document.getElementById('game-editor-esper-inventory-title'),
  gameEditorEsperInventoryList: document.getElementById('game-editor-esper-inventory-list'),
  gameEditorBlitzesTitle: document.getElementById('game-editor-blitzes-title'),
  gameEditorBlitzesList: document.getElementById('game-editor-blitzes-list'),
  gameEditorSwdtechsTitle: document.getElementById('game-editor-swdtechs-title'),
  gameEditorSwdtechsList: document.getElementById('game-editor-swdtechs-list'),
  gameEditorRagesTitle: document.getElementById('game-editor-rages-title'),
  gameEditorRagesList: document.getElementById('game-editor-rages-list'),
  gameEditorDancesTitle: document.getElementById('game-editor-dances-title'),
  gameEditorDancesList: document.getElementById('game-editor-dances-list'),
  gameEditorGpTitle: document.getElementById('game-editor-gp-title'),
  gameEditorGpRow: document.getElementById('game-editor-gp-row'),
  gameEditorGpInput: document.getElementById('game-editor-gp-input'),
  gameEditorInventoryTitle: document.getElementById('game-editor-inventory-title'),
  gameEditorInventoryList: document.getElementById('game-editor-inventory-list'),
  gameEditorKeyItemsTitle: document.getElementById('game-editor-key-items-title'),
  gameEditorKeyItemsList: document.getElementById('game-editor-key-items-list'),
  gameEditorItemBonusTitle: document.getElementById('game-editor-item-bonus-title'),
  gameEditorItemBonusHint: document.getElementById('game-editor-item-bonus-hint'),
  gameEditorItemBonusBody: document.getElementById('game-editor-item-bonus-body'),
  btnGameEditorRefresh: document.getElementById('btn-game-editor-refresh'),
  cheatPresets: document.getElementById('cheat-presets'),
  cheatPresetList: document.getElementById('cheat-preset-list'),
  cheatAddForm: document.getElementById('cheat-add-form'),
  cheatCodeInput: document.getElementById('cheat-code-input'),
  cheatDescInput: document.getElementById('cheat-desc-input'),
  btnCheatAdd: document.getElementById('btn-cheat-add'),
  remapList: document.getElementById('remap-list'),
  btnResetMapping: document.getElementById('btn-reset-mapping'),
  gamepadDebug: document.getElementById('gamepad-debug'),
  modifierBindingDisplay: document.getElementById('modifier-binding-display'),
  btnModifierListen: document.getElementById('btn-modifier-listen'),
  btnModifierClear: document.getElementById('btn-modifier-clear'),
  modifierComboList: document.getElementById('modifier-combo-list'),
  modifierAddForm: document.getElementById('modifier-add-form'),
  modifierTargetSelect: document.getElementById('modifier-target-select'),
  modifierActionSelect: document.getElementById('modifier-action-select'),
  btnModifierRecord: document.getElementById('btn-modifier-record'),
  btnModifierReset: document.getElementById('btn-modifier-reset'),
  macroRecordingHint: document.getElementById('macro-recording-hint'),
  macroRecordingLive: document.getElementById('macro-recording-live'),
  macroStepButtons: document.getElementById('macro-step-buttons'),
  macroStepDuration: document.getElementById('macro-step-duration'),
  btnMacroAddStep: document.getElementById('btn-macro-add-step'),
  macroStepList: document.getElementById('macro-step-list'),
  btnMacroTest: document.getElementById('btn-macro-test'),
  btnMacroClearSteps: document.getElementById('btn-macro-clear-steps'),
  btnMacroSave: document.getElementById('btn-macro-save'),
  macroBuilderDetails: document.getElementById('macro-builder-details'),
  gamepadStatus: document.getElementById('gamepad-status'),
  log: document.getElementById('log'),
  migrationStatus: document.getElementById('migration-status'),
};

const ctx2d = el.canvas.getContext('2d', { alpha: false });

// The core's framebuffer is blitted here at native SNES resolution first;
// presentFrame() then decides how to project that onto the visible canvas
// (1:1, or integer-upscaled for the sharp-bilinear filter). Keeping the
// native copy separate means the visible canvas's backing size can change
// with the filter/window without touching the per-frame blit logic.
const offscreen = document.createElement('canvas');
const offscreenCtx = offscreen.getContext('2d', { alpha: false });

const FILTER_STORAGE_KEY = 'snes9x-web:video-filter';
const SCANLINES_STORAGE_KEY = 'snes9x-web:scanlines';
const ADVANCED_VIDEO_STORAGE_KEY = 'snes9x-web:advanced-video';
const ADVANCED_FILTER_STORAGE_KEY = 'snes9x-web:advanced-filter';
const TOUCH_CONTROLS_STORAGE_KEY = 'snes9x-web:touch-controls';
const AUDIO_DURING_FF_STORAGE_KEY = 'snes9x-web:audio-during-fastforward';
const FAST_FORWARD_MULTIPLIER = 3;
const BASE_MAX_FRAMES_PER_TICK = 4;
const SAVE_STATE_SLOT_COUNT = 200;

/* ---------------------------------------------------------------- state -- */

const state = {
  core: null,
  audio: new AudioOutput(),
  input: null,
  running: false,
  rafId: null,
  currentGame: null, // { id, name }
  cheats: [], // cheat records for currentGame, from storage.listCheats()
  gameEditor: null, // matching game-editors/ plugin for the loaded ROM, or null
  gameEditorItemBonusId: null, // which item is selected in the item-bonus editor, persisted across re-renders
  gameEditorItemBonusSearch: '', // item-bonus picker's search box text, persisted across re-renders
  // Which Item Editor flag groups are expanded. Every flag toggle re-renders
  // the whole panel, so without this a group would snap shut after each tick.
  gameEditorOpenFlagGroups: new Set(),
  gameEditorInventorySearch: '', // inventory "Add item" picker's search box text, persisted across re-renders
  imageData: null,
  lastFrameTime: 0,
  frameAccumulator: 0,
  fpsCounter: { frames: 0, since: 0 },
  fastForward: false,
  audioDuringFastForward: localStorage.getItem(AUDIO_DURING_FF_STORAGE_KEY) === '1',
  videoFilter: localStorage.getItem(FILTER_STORAGE_KEY) || 'pixelated',
  scanlines: localStorage.getItem(SCANLINES_STORAGE_KEY) === '1',
  // Advanced video (WebGL shader upscaling) is opt-in and off by default --
  // glRenderer is only ever constructed the first time a user enables it.
  advancedVideo: localStorage.getItem(ADVANCED_VIDEO_STORAGE_KEY) === '1',
  advancedFilter: localStorage.getItem(ADVANCED_FILTER_STORAGE_KEY) || ADVANCED_FILTERS[0].id,
  glRenderer: null,
  syncSession: null,
  // Same lazy-init pattern as glRenderer: the overlay's DOM isn't built
  // until a user turns this on.
  touchControlsEnabled: localStorage.getItem(TOUCH_CONTROLS_STORAGE_KEY) === '1',
  touchControls: null,
};

/**
 * Surfaced to tests and to the console. Playwright asserts against this rather
 * than scraping the DOM, and it is genuinely useful when debugging by hand.
 */
const appStatus = {
  ready: false,
  coreLoaded: false,
  isStub: null,
  romLoaded: false,
  gameName: null,
  frames: 0,
  error: null,
  serviceWorker: 'unregistered',
};
window.__snes9x = { status: appStatus, state, storage, sync };

function log(message, isError = false) {
  el.log.textContent = message;
  el.log.classList.toggle('error', isError);
  (isError ? console.error : console.log)(`[snes9x-web] ${message}`);
}

/**
 * The one-time save metadata/blob split (storage.migrateSaveBlobs()) runs in
 * the background and can take a while on a big library, especially on a
 * phone -- so it says so on screen instead of only in a console nobody on a
 * phone can see. Its own line, never log(): this must not overwrite (or be
 * overwritten by) the normal single-slot status message.
 */
function setMigrationStatus(message, done = false) {
  el.migrationStatus.hidden = message === null;
  el.migrationStatus.textContent = message ?? '';
  el.migrationStatus.classList.toggle('done', done);
}

function showMigrationProgress(done, total) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  setMigrationStatus(`Optimising save storage… ${done}/${total} (${pct}%) — safe to keep playing, or to close the app.`);
}

function finishMigrationProgress(migrated) {
  if (migrated === 0) {
    setMigrationStatus(null);
    return;
  }
  setMigrationStatus(`Save storage optimised (${migrated} save${migrated === 1 ? '' : 's'}).`, true);
  setTimeout(() => setMigrationStatus(null), 6000);
}

let toastTimer = null;

/**
 * Brief on-screen confirmation overlaid on the video, separate from the
 * persistent #log status line below the controls -- for the kind of
 * feedback (slot changed, state saved/loaded) you want to notice in the
 * corner of your eye without it lingering. Retriggering while one is still
 * showing just restarts the fade-out timer with the new message.
 */
function showToast(message) {
  el.toast.textContent = message;
  el.toast.classList.add('toast-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.toast.classList.remove('toast-visible'), 1800);
}

/** Unlike showToast(), this stays up for as long as the game is actually paused. */
function setPausedOverlay(visible) {
  el.pauseOverlay.hidden = !visible;
}

/* ----------------------------------------------------------------- boot -- */

async function boot() {
  log('Loading emulator core…');

  try {
    state.core = await loadCore();
    state.core.init();
  } catch (err) {
    appStatus.error = String(err?.message ?? err);
    el.coreBadge.textContent = 'core: failed';
    el.coreBadge.className = 'badge badge-err';
    log(`Failed to initialise the emulator core: ${appStatus.error}`, true);
    return;
  }

  appStatus.coreLoaded = true;
  appStatus.isStub = state.core.isStub;

  if (state.core.isStub) {
    el.coreBadge.textContent = 'core: STUB (not the real emulator)';
    el.coreBadge.className = 'badge badge-stub';
    if (state.core.stubReason) el.coreBadge.title = state.core.stubReason;
    // stubReason is set only when the real core was actually attempted and
    // failed (as opposed to a dev checkout that never built one at all) --
    // surfaced here, not just console.warn'd, since checking devtools isn't
    // practical on every device (a phone, especially). A "core export
    // missing" reason specifically means a service-worker-cached core is
    // stale relative to this JS bundle -- try a hard refresh (or, if the
    // problem persists, clearing this site's storage/cache) rather than
    // "build the real one", which only makes sense for a dev checkout.
    log(
      state.core.stubReason
        ? `Running against the stub core — the real core failed to load (${state.core.stubReason}). Try a hard refresh; if it persists, clear this site's storage/cache and reload.`
        : 'Running against the stub core — run core/build.sh to build the real one.',
      Boolean(state.core.stubReason),
    );
  } else {
    el.coreBadge.textContent = 'core: snes9x (wasm)';
    el.coreBadge.className = 'badge badge-ok';
    log('Emulator core ready. Load a ROM to start.');
  }

  state.input = new InputManager(state.core, { onHotkey: handleHotkey });
  state.input.attach();

  // Independent of the emulator loop (tick()), which stops running entirely
  // while paused (state.running is false, so it never even gets scheduled
  // again) -- polling gamepads only from inside it meant a gamepad press
  // could never un-pause the game once paused, since nothing was asking the
  // browser for gamepad state anymore. Polling once per real animation
  // frame, always, decouples "is a button held" from "how many emulated
  // frames ran this tick" entirely -- pollGamepads() flushes to the core
  // regardless, and a paused core just doesn't call runFrame() to consume it.
  requestAnimationFrame(function pollInputLoop() {
    state.input?.pollGamepads();
    requestAnimationFrame(pollInputLoop);
  });

  wireUi();
  registerServiceWorker();

  await storage.requestPersistence();
  await refreshRomList();

  appStatus.ready = true;
  document.body.dataset.ready = 'true';

  // Deliberately NOT awaited: the DB_VERSION 3 save metadata/blob split moves
  // existing blobs one record at a time, in the background, after the app is
  // already usable (see storage.migrateSaveBlobs()). A big library takes a
  // while, and nothing needs to wait for it -- every read handles both the
  // migrated and not-yet-migrated shape. Safe to interrupt; the next boot
  // picks up whatever is left.
  storage
    .migrateSaveBlobs(({ done, total }) => showMigrationProgress(done, total))
    .then(({ migrated }) => finishMigrationProgress(migrated))
    .catch((err) => {
      console.warn('[snes9x-web] save blob migration failed (will retry next load)', err);
      setMigrationStatus('Optimising save storage didn’t finish — it will pick up where it left off next time.');
      setTimeout(() => setMigrationStatus(null), 8000);
    });
}

/* ------------------------------------------------------------------- ui -- */

function wireUi() {
  for (let slot = 1; slot <= SAVE_STATE_SLOT_COUNT; slot++) {
    const opt = document.createElement('option');
    opt.value = String(slot);
    opt.textContent = String(slot);
    el.slotSelect.append(opt);
  }
  el.slotSelect.addEventListener('change', onSlotChanged);

  el.romInput.addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (file) await loadRomFile(file);
    event.target.value = ''; // so re-picking the same file fires change again
  });

  el.saveImportInput.addEventListener('change', async (event) => {
    const file = event.target.files?.[0];
    if (file) await importSaveFile(file);
    event.target.value = ''; // so re-picking the same file fires change again
  });

  el.btnSaveDownloadAll.addEventListener('click', () => exportAllSaves());

  el.btnSaveDeleteAll.addEventListener('click', async () => {
    if (!state.currentGame) return;
    const saves = await storage.listSaves(state.currentGame.id);
    if (saves.length === 0) return;

    el.deleteAllSavesMessage.textContent =
      `This will permanently delete all ${saves.length} save state${saves.length === 1 ? '' : 's'} for "${state.currentGame.name}"` +
      (state.syncSession ? ', including any copies synced to the cloud.' : '.');
    el.deleteAllSavesConfirmCheckbox.checked = false;
    el.btnDeleteAllSavesConfirm.disabled = true;
    el.deleteAllSavesDialog.showModal();
  });

  el.deleteAllSavesConfirmCheckbox.addEventListener('change', () => {
    el.btnDeleteAllSavesConfirm.disabled = !el.deleteAllSavesConfirmCheckbox.checked;
  });

  el.btnDeleteAllSavesCancel.addEventListener('click', () => el.deleteAllSavesDialog.close());

  el.btnDeleteAllSavesConfirm.addEventListener('click', async () => {
    el.deleteAllSavesDialog.close();
    await deleteAllSaveStates();
  });

  // Drag and drop. dragover must be prevented on every event or the browser
  // navigates away to the dropped file instead of giving it to us.
  for (const type of ['dragenter', 'dragover']) {
    el.stage.addEventListener(type, (event) => {
      event.preventDefault();
      el.stage.classList.add('dragover');
    });
  }
  for (const type of ['dragleave', 'drop']) {
    el.stage.addEventListener(type, (event) => {
      event.preventDefault();
      el.stage.classList.remove('dragover');
    });
  }
  el.stage.addEventListener('drop', async (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (file) await loadRomFile(file);
  });
  // A drop anywhere else would otherwise navigate the page to the file.
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => e.preventDefault());

  el.btnPause.addEventListener('click', () => togglePause());
  el.btnReset.addEventListener('click', () => resetGame());
  el.btnMute.addEventListener('click', () => toggleMute());
  el.btnSaveState.addEventListener('click', () => saveState(currentSlot()));
  el.btnLoadState.addEventListener('click', () => loadState(currentSlot()));

  el.btnFullscreen.addEventListener('click', () => toggleFullscreen());
  document.addEventListener('fullscreenchange', updateFullscreenButton);
  document.addEventListener('webkitfullscreenchange', updateFullscreenButton);

  el.filterSelect.value = state.videoFilter;
  el.filterSelect.addEventListener('change', () => {
    state.videoFilter = el.filterSelect.value;
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, state.videoFilter);
    } catch {
      /* storage unavailable */
    }
    if (appStatus.romLoaded) blit(); // redraw immediately even if paused
  });

  el.scanlinesToggle.checked = state.scanlines;
  el.stage.classList.toggle('scanlines-on', state.scanlines);
  el.scanlinesToggle.addEventListener('change', () => {
    state.scanlines = el.scanlinesToggle.checked;
    try {
      localStorage.setItem(SCANLINES_STORAGE_KEY, state.scanlines ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    el.stage.classList.toggle('scanlines-on', state.scanlines);
  });

  el.ffAudioToggle.checked = state.audioDuringFastForward;
  state.audio.setAudioDuringFastForward(state.audioDuringFastForward);
  el.ffAudioToggle.addEventListener('change', () => {
    state.audioDuringFastForward = el.ffAudioToggle.checked;
    try {
      localStorage.setItem(AUDIO_DURING_FF_STORAGE_KEY, state.audioDuringFastForward ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    state.audio.setAudioDuringFastForward(state.audioDuringFastForward);
  });

  el.advancedVideoToggle.checked = state.advancedVideo;
  el.advancedFilterPicker.hidden = !state.advancedVideo;
  if (state.advancedVideo) ensureGlRenderer();
  el.advancedVideoToggle.addEventListener('change', () => {
    state.advancedVideo = el.advancedVideoToggle.checked;
    try {
      localStorage.setItem(ADVANCED_VIDEO_STORAGE_KEY, state.advancedVideo ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    el.advancedFilterPicker.hidden = !state.advancedVideo;
    if (state.advancedVideo) ensureGlRenderer();
    if (appStatus.romLoaded) blit();
  });

  el.advancedFilterSelect.addEventListener('change', () => {
    state.advancedFilter = el.advancedFilterSelect.value;
    try {
      localStorage.setItem(ADVANCED_FILTER_STORAGE_KEY, state.advancedFilter);
    } catch {
      /* storage unavailable */
    }
    if (appStatus.romLoaded) blit();
  });

  el.touchControlsToggle.checked = state.touchControlsEnabled;
  el.btnTouchEdit.hidden = !state.touchControlsEnabled;
  el.btnTouchReset.hidden = !state.touchControlsEnabled;
  if (state.touchControlsEnabled) ensureTouchControls().setEnabled(true);
  el.touchControlsToggle.addEventListener('change', () => {
    state.touchControlsEnabled = el.touchControlsToggle.checked;
    try {
      localStorage.setItem(TOUCH_CONTROLS_STORAGE_KEY, state.touchControlsEnabled ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
    el.btnTouchEdit.hidden = !state.touchControlsEnabled;
    el.btnTouchReset.hidden = !state.touchControlsEnabled;
    ensureTouchControls().setEnabled(state.touchControlsEnabled);
  });

  el.btnTouchEdit.addEventListener('click', () => {
    const editing = !state.touchControls?.editMode;
    ensureTouchControls().setEditMode(editing);
    el.btnTouchEdit.textContent = editing ? 'Done editing' : 'Edit layout';
    el.btnTouchEdit.classList.toggle('button-active', editing);
  });

  el.btnTouchReset.addEventListener('click', () => {
    ensureTouchControls().resetLayout();
    log('On-screen control layout reset to defaults.');
  });

  el.btnResetMapping.addEventListener('click', () => {
    state.input.resetBindings();
    renderRemapPanel();
    log('Gamepad mapping reset to defaults.');
  });
  renderRemapPanel();

  wireModifier();

  wireCheats();
  wireGameEditor();

  wireSync();

  window.addEventListener('gamepadconnected', updateGamepadStatus);
  window.addEventListener('gamepaddisconnected', updateGamepadStatus);
  updateGamepadStatus();

  // Independent of the emulator loop (which only polls gamepads while a ROM
  // is running) so a controller can be checked/diagnosed before loading one.
  setInterval(updateGamepadDebug, 150);

  // Pause when the tab is hidden: rAF stops firing anyway, and this keeps the
  // audio queue from being flooded on the way back.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && state.running) togglePause();
  });
}

function currentSlot() {
  return Number(el.slotSelect.value) || 1;
}

/**
 * Called from every place the selected slot can change (the dropdown's own
 * 'change' event, the on-screen Slot+/Slot- buttons, the 1-4 keyboard
 * shortcuts, and modifier-bound slot actions): keeps the on-screen Slot+
 * button's label showing the current number, and remembers it per-game so
 * it's restored next time this ROM loads.
 *
 * `announce: false` skips the toast -- used by loadState()'s dropdown sync,
 * where "Loaded slot N" already says it; a "Slot N" toast right alongside
 * would just be noise.
 */
function onSlotChanged({ announce = true } = {}) {
  state.touchControls?.setLabel('SLOT', `Slot+ ${currentSlot()}`);
  if (state.currentGame) storage.setLastSlot(state.currentGame.id, currentSlot());
  if (announce) announceSlot();
}

/** Toast for a slot change: whether it's empty, or when it was last saved. Fire-and-forget. */
async function announceSlot() {
  const slot = currentSlot();
  if (!state.currentGame) {
    showToast(`Slot ${slot}`);
    return;
  }
  try {
    const meta = await storage.getSaveStateMeta(state.currentGame.id, slot);
    showToast(meta ? `Slot ${slot} · ${new Date(meta.updatedAt).toLocaleString()}` : `Slot ${slot} · empty`);
  } catch {
    showToast(`Slot ${slot}`);
  }
}

function updateGamepadStatus() {
  const n = state.input?.gamepadCount ?? 0;
  el.gamepadStatus.textContent = n === 0 ? 'none detected' : `${n} connected`;
}

/**
 * Live raw button/axis values for the primary connected pad. Exists so a
 * controller that isn't recognised as the "standard" Gamepad API layout --
 * cheap/generic ones especially -- can be diagnosed: a control that never
 * changes any number here never reaches the browser at all (firmware-level,
 * nothing this app can bind); one that only moves a value in the axes list
 * is exactly what the axis-binding support in input.js's Listen capture is
 * for.
 */
function updateGamepadDebug() {
  const info = state.input?.getPrimaryPadDebugState();
  if (!info) {
    el.gamepadDebug.textContent = 'no gamepad detected';
    return;
  }
  el.gamepadDebug.textContent =
    `${info.id}\n` +
    `mapping: ${info.mapping}\n` +
    `buttons: [${info.buttons.join(', ')}]\n` +
    `axes:    [${info.axes.join(', ')}]`;
}

/** Fullscreen just the stage (canvas + its overlays), not the whole page. */
function toggleFullscreen() {
  const active = document.fullscreenElement || document.webkitFullscreenElement;
  if (!active) {
    const request = el.stage.requestFullscreen || el.stage.webkitRequestFullscreen;
    request?.call(el.stage)?.catch?.((err) => {
      log(`Could not enter fullscreen: ${err?.message ?? err}`, true);
    });
  } else {
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    exit?.call(document);
  }
}

function updateFullscreenButton() {
  const active = document.fullscreenElement === el.stage || document.webkitFullscreenElement === el.stage;
  el.btnFullscreen.textContent = active ? 'Exit fullscreen' : 'Fullscreen';
}

/** Lazily create the on-screen touch overlay the first time it's enabled. Returns it either way. */
function ensureTouchControls() {
  if (!state.touchControls) {
    state.touchControls = new TouchControls(el.stage, state.input, { onTrigger: handleTouchTrigger });
    onSlotChanged({ announce: false }); // otherwise the Slot+ button shows its static default label until the slot next changes
  }
  return state.touchControls;
}

/** One-shot on-screen buttons (save/load/next-slot) that aren't SNES buttons or held actions. */
function handleTouchTrigger(name) {
  // saveState/loadState/nextSlot are exactly what handleHotkey() already
  // does for the matching keyboard shortcuts -- no reason to duplicate it.
  handleHotkey(name);
}

/** Lazily create the WebGL context/shaders the first time advanced video is enabled. */
function ensureGlRenderer() {
  if (state.glRenderer) return;
  state.glRenderer = new GLFilterRenderer(el.canvasGl);
  if (!state.glRenderer.available) {
    log(`Advanced video unavailable: ${state.glRenderer.failureFor('xbr') ?? 'WebGL2 is not supported here.'}`, true);
  }
  populateAdvancedFilterOptions();
}

function populateAdvancedFilterOptions() {
  el.advancedFilterSelect.replaceChildren();
  for (const { id, label } of ADVANCED_FILTERS) {
    const ready = state.glRenderer?.isFilterReady(id) ?? false;
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = ready ? label : `${label} (unavailable)`;
    opt.disabled = !ready;
    if (!ready) opt.title = state.glRenderer?.failureFor(id) ?? '';
    el.advancedFilterSelect.append(opt);
  }
  el.advancedFilterSelect.value = state.advancedFilter;
}

/* ------------------------------------------------------------- sync -- */

function setSyncStatus(text, isError = false) {
  el.syncStatus.textContent = text;
  el.syncStatus.classList.toggle('error', isError);
}

/**
 * Reflect the current auth session in the Cloud sync panel. The sign-in form
 * is hidden unless there's actually a Supabase project to sign in TO --
 * without that check a credential-less deployment shows a live "Send magic
 * link" button whose submit handler was never wired, so pressing it does a
 * native form submit and reloads the page.
 */
function updateSyncUI(session) {
  state.syncSession = session;
  const configured = sync.isConfigured();
  el.syncSignedIn.hidden = !session;
  el.syncSigninForm.hidden = Boolean(session) || !configured;
  if (session) el.syncUserEmail.textContent = session.user.email;
}

/**
 * (Re)subscribe to auth changes for whichever project is configured now.
 * Called again after a config change so switching projects -- including
 * enabling sync for the first time on a build that ships no credentials --
 * takes effect immediately instead of needing a page reload.
 */
let unsubscribeAuth = null;
function attachAuthListener() {
  unsubscribeAuth?.();
  unsubscribeAuth = sync.onAuthStateChange((session) => {
    updateSyncUI(session);
    if (session) runSync({ silent: true });
  });

  sync.getSession().then((session) => {
    updateSyncUI(session);
    // A magic-link redirect lands here with auth tokens in the URL; once the
    // client's parsed them (triggering onAuthStateChange above) they're no
    // longer needed and are best not left sitting in the address bar/history.
    if ((window.location.hash || window.location.search) && session) {
      history.replaceState(null, '', window.location.pathname);
    }
  });
}

const SYNC_PHASE_LABELS = {
  'push-saves': 'pushing save states',
  'push-roms': 'pushing ROMs',
  'push-cheats': 'pushing cheats',
  'pull-saves': 'pulling save states',
  'pull-cheats': 'pulling cheats',
  'list-roms': 'checking cloud ROMs',
  wipe: 'wiping local data',
  'pull-roms': 'downloading ROMs',
};

/**
 * Race a promise against a hard deadline, so a sync that hangs for ANY
 * reason -- network, a stuck IndexedDB transaction, anything -- still ends
 * with a visible, actionable status instead of leaving the UI stuck on
 * "Syncing…" forever with no way to tell what (if anything) is still
 * happening. `label` is folded into the timeout error message.
 */
function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s -- check your connection and try again.`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** Push anything dirty, pull anything new, and refresh the save list if it changed. */
async function runSync({ silent = false } = {}) {
  if (!sync.isConfigured() || !state.syncSession) return;
  if (!silent) setSyncStatus('Syncing…');
  try {
    const onProgress = (info) => {
      if (silent) return;
      const label = SYNC_PHASE_LABELS[info.phase] ?? info.phase;
      const count = info.total ? ` (${info.done}/${info.total})` : '';
      setSyncStatus(`Syncing… ${label}${count}`);
    };

    const { pushed, pushedRoms, pushedCheats, pulled, pulledCheats } = await withTimeout(
      sync.syncNow(onProgress),
      30_000,
      'Sync',
    );
    const anything = pushed || pushedRoms || pushedCheats || pulled || pulledCheats;

    if (pulled) await refreshSaveList();
    if (pulledCheats) await refreshCheatList();
    // Cheap either way (metadata only): reveals ROMs another device has
    // pushed since this device last checked, not just ones this call pushed.
    if (!silent) setSyncStatus('Syncing… checking cloud ROMs');
    await withTimeout(refreshRomList(), 15_000, 'Checking cloud ROMs');

    if (!silent || anything) {
      setSyncStatus(
        anything
          ? `Synced: ${pushed ?? 0} save${pushed === 1 ? '' : 's'} pushed, ${pushedRoms ?? 0} ROM${pushedRoms === 1 ? '' : 's'} pushed, ${pushedCheats ?? 0} cheat${pushedCheats === 1 ? '' : 's'} pushed, ${pulled ?? 0} save${pulled === 1 ? '' : 's'} pulled, ${pulledCheats ?? 0} cheat${pulledCheats === 1 ? '' : 's'} pulled.`
          : 'Already up to date.',
      );
    }
  } catch (err) {
    setSyncStatus(`Sync failed: ${err?.message ?? err}`, true);
  }
}

/**
 * Disaster recovery: wipe local storage entirely and restore from the
 * cloud. Whatever ROM is currently running has its local record deleted out
 * from under it by sync.forcePull(), so stop and unload it first -- same
 * teardown the "Delete ROM" library action already uses.
 */
async function runForcePull() {
  if (appStatus.romLoaded) {
    stopLoop();
    setPausedOverlay(false);
    state.core.unloadRom();
    state.currentGame = null;
    state.gameEditor = null;
    renderGameEditor();
    appStatus.romLoaded = false;
    el.dropHint.hidden = false;
  }

  setSyncStatus('Force pulling…');
  try {
    const onProgress = (info) => {
      const label = SYNC_PHASE_LABELS[info.phase] ?? info.phase;
      const count = info.total ? ` (${info.done}/${info.total})` : '';
      setSyncStatus(`Force pulling… ${label}${count}`);
    };
    const { pulled, pulledCheats, pulledRoms } = await withTimeout(sync.forcePull(onProgress), 180_000, 'Force pull');
    setSyncStatus(
      `Force pull complete: ${pulledRoms ?? 0} ROM${pulledRoms === 1 ? '' : 's'}, ${pulled ?? 0} save${pulled === 1 ? '' : 's'}, ${pulledCheats ?? 0} cheat${pulledCheats === 1 ? '' : 's'} restored.`,
    );
  } catch (err) {
    setSyncStatus(`Force pull failed: ${err?.message ?? err}`, true);
  }

  await refreshRomList();
  await refreshSaveList();
  await refreshCheatList();
}

/**
 * Reflect which Supabase project is in effect, and whether there's a
 * built-in one to fall back to. Called on load and after any config change.
 */
function renderSyncConfig() {
  const config = sync.getConfig();
  const host = config ? config.url.replace(/^https:\/\//, '') : null;
  el.syncConfigCurrent.textContent = !config
    ? 'This site ships with no Supabase project, so sync is off until you add your own below.'
    : config.custom
      ? `Using your own project (${host}).`
      : `Using this site's shared project (${host}).`;
  // Only meaningful when there's something to fall back TO.
  el.btnSyncConfigClear.hidden = !(config?.custom && sync.hasBuiltInConfig());
  if (config?.custom) {
    el.syncConfigUrl.value = config.url;
    el.syncConfigKey.value = config.key;
  }
  updateSyncUI(state.syncSession);
}

function wireSync() {
  // The panel is shown even with no built-in credentials: the whole point of
  // the bring-your-own form is that a visitor can enable sync on a
  // deployment that ships without any, so hiding it would hide the only way
  // to turn sync on.
  el.syncPanel.hidden = false;
  renderSyncConfig();

  el.syncConfigForm.addEventListener('submit', (event) => {
    event.preventDefault();
    try {
      sync.setCustomConfig(el.syncConfigUrl.value, el.syncConfigKey.value);
    } catch (err) {
      setSyncStatus(`${err?.message ?? err}`, true);
      return;
    }
    renderSyncConfig();
    setSyncStatus('Now using your own Supabase project. Sign in to start syncing.');
    // A different project means a different auth session, so whatever this
    // page thought it knew about the old one no longer applies -- and the
    // listener has to be rebuilt against the new client.
    updateSyncUI(null);
    attachAuthListener();
  });

  el.btnSyncConfigClear.addEventListener('click', () => {
    sync.clearCustomConfig();
    el.syncConfigUrl.value = '';
    el.syncConfigKey.value = '';
    renderSyncConfig();
    updateSyncUI(null);
    attachAuthListener();
    setSyncStatus("Back to this site's default Supabase project.");
  });

  // Everything below is wired unconditionally, even with no project
  // configured: the sync.* calls are all no-ops without a client, and wiring
  // them up front is what lets a freshly-entered config work immediately
  // rather than only after a reload.

  el.syncSigninForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = el.syncEmail.value.trim();
    if (!email) return;
    setSyncStatus('Sending magic link…');
    try {
      await sync.signInWithEmail(email);
      setSyncStatus(`Check ${email} for a sign-in link.`);
    } catch (err) {
      setSyncStatus(`Could not send link: ${err?.message ?? err}`, true);
    }
  });

  el.btnSyncNow.addEventListener('click', () => runSync());

  el.btnSyncSignout.addEventListener('click', async () => {
    try {
      await sync.signOut();
      setSyncStatus('Signed out.');
    } catch (err) {
      setSyncStatus(`Sign out failed: ${err?.message ?? err}`, true);
    }
  });

  // Two-click confirm: destructive (wipes every local ROM/save/cheat), so a
  // single accidental click must not fire it. First click arms the button
  // and visibly changes it (text + danger styling); a second click within
  // FORCE_PULL_ARM_MS actually runs it. Any other UI interaction re-disarms
  // it via disarmForcePull() below, rather than requiring the timer to
  // expire, so switching attention away is itself a "no, cancel".
  const FORCE_PULL_ARM_MS = 6000;
  let forcePullArmTimer = null;
  const disarmForcePull = () => {
    clearTimeout(forcePullArmTimer);
    forcePullArmTimer = null;
    el.btnForcePull.textContent = 'Force pull from cloud';
    el.btnForcePull.classList.remove('danger');
  };
  el.btnForcePull.addEventListener('click', () => {
    if (!forcePullArmTimer) {
      el.btnForcePull.textContent = 'Click again to wipe local data and pull from cloud';
      el.btnForcePull.classList.add('danger');
      forcePullArmTimer = setTimeout(disarmForcePull, FORCE_PULL_ARM_MS);
      return;
    }
    disarmForcePull();
    runForcePull();
  });

  attachAuthListener();
}

/** (Re)build the gamepad remap panel from the input manager's current bindings. */
function renderRemapPanel() {
  const bindings = state.input.getBindings();
  el.remapList.replaceChildren();

  const addRow = (kind, name, label) => {
    const current = kind === 'button' ? bindings.buttons[name] : bindings.actions[name];

    const li = document.createElement('li');

    const nameEl = document.createElement('span');
    nameEl.className = 'name';
    nameEl.textContent = label;

    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = describeBinding(current) ?? 'unbound';

    const listen = document.createElement('button');
    listen.textContent = 'Listen…';
    listen.addEventListener('click', async () => {
      listen.disabled = true;
      listen.textContent = 'Press a button or trigger…';
      const captured = await state.input.startListening({ kind, name });
      renderRemapPanel();
      if (captured !== null) log(`${label} bound to gamepad ${describeBinding(captured)}.`);
    });

    const clear = document.createElement('button');
    clear.className = 'danger';
    clear.textContent = 'Clear';
    clear.disabled = current === null || current === undefined;
    clear.addEventListener('click', () => {
      if (kind === 'button') state.input.clearButtonBinding(name);
      else state.input.clearActionBinding(name);
      renderRemapPanel();
    });

    li.append(nameEl, meta, listen, clear);
    el.remapList.append(li);
  };

  for (const { name, label } of REMAPPABLE_BUTTONS) addRow('button', name, label);
  for (const { name, label } of ACTIONS) addRow('action', name, label);
}

/**
 * A modifier-combo target is either an SNES button name (e.g. "A", stored in
 * InputManager.modifiedBindings, unchanged from before this feature existed)
 * or a raw gamepad binding value -- a button index or axis string -- for a
 * physical control that isn't mapped to any SNES button or action at all
 * (stored in InputManager.extraBindings, e.g. a spare stick button no game
 * control needs). The single #modifier-target-select carries both kinds of
 * option; these two functions are the only place that need to know the
 * encoding, so every save/edit/remove call site just decodes the current
 * selection once and branches on the result.
 */
const EXTRA_TARGET_PREFIX = 'extra:';
const EXTRA_CAPTURE_OPTION_VALUE = '__capture_extra__';

function encodeExtraTarget(value) {
  return `${EXTRA_TARGET_PREFIX}${value}`;
}

/** @returns the raw binding value if `target` is an extra-target encoding, else null (an SNES button name). */
function decodeExtraTarget(target) {
  if (!target.startsWith(EXTRA_TARGET_PREFIX)) return null;
  return parseExtraKey(target.slice(EXTRA_TARGET_PREFIX.length));
}

/** Human-readable label for whatever's currently selected as a modifier-combo target, for log messages. */
function describeModifierTarget(target) {
  const extraValue = decodeExtraTarget(target);
  return extraValue !== null ? describeBinding(extraValue) : target;
}

/**
 * (Re)builds #modifier-target-select: every SNES button (always available,
 * static), every already-captured extra/spare-button target (from
 * InputManager.getExtraBindings()), and a trailing "+ Listen…" option that
 * triggers a fresh capture when chosen. `selectValue`, if given, is
 * selected afterward if present among the options -- also added as an extra
 * option first if it encodes a just-captured value with no binding yet
 * (Bind action/Record macro/manual Save haven't run for it, so it wouldn't
 * otherwise appear in getExtraBindings() yet).
 */
function populateModifierTargetSelect(selectValue) {
  const prevValue = selectValue ?? el.modifierTargetSelect.value;
  el.modifierTargetSelect.replaceChildren();

  const snesGroup = document.createElement('optgroup');
  snesGroup.label = 'SNES buttons';
  for (const { name, label } of REMAPPABLE_BUTTONS) {
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = label;
    snesGroup.append(opt);
  }
  el.modifierTargetSelect.append(snesGroup);

  const extraKeys = new Set(Object.keys(state.input.getExtraBindings()));
  const pendingValue = decodeExtraTarget(prevValue ?? '');
  if (pendingValue !== null) extraKeys.add(String(pendingValue));

  if (extraKeys.size > 0) {
    const extraGroup = document.createElement('optgroup');
    extraGroup.label = 'Spare buttons';
    for (const key of extraKeys) {
      const value = parseExtraKey(key);
      const opt = document.createElement('option');
      opt.value = encodeExtraTarget(value);
      opt.textContent = describeBinding(value);
      extraGroup.append(opt);
    }
    el.modifierTargetSelect.append(extraGroup);
  }

  const captureOpt = document.createElement('option');
  captureOpt.value = EXTRA_CAPTURE_OPTION_VALUE;
  captureOpt.textContent = '+ Listen for a spare button…';
  el.modifierTargetSelect.append(captureOpt);

  if (prevValue && [...el.modifierTargetSelect.options].some((o) => o.value === prevValue)) {
    el.modifierTargetSelect.value = prevValue;
  }
}

/** One-time setup for the "Modifier & macros" panel: populate selects, wire buttons, render current state. */
function wireModifier() {
  populateModifierTargetSelect();
  for (const { name, label } of MODIFIABLE_ACTIONS) {
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = label;
    el.modifierActionSelect.append(opt);
  }

  el.modifierTargetSelect.addEventListener('change', async () => {
    if (el.modifierTargetSelect.value !== EXTRA_CAPTURE_OPTION_VALUE) return;

    el.modifierTargetSelect.disabled = true;
    const captured = await state.input.startListening({ kind: 'extra' });
    el.modifierTargetSelect.disabled = false;

    if (captured === null) {
      populateModifierTargetSelect(); // capture cancelled/timed out -- fall back to a sane default selection
      log('No input captured for a spare-button trigger.', true);
      return;
    }
    // Not bound to anything yet (nothing calls setExtraAction/setExtraMacro
    // until Bind action/Record macro/manual Save runs) -- listed as a
    // selectable target immediately anyway, so the user can pick what to
    // attach to it right away without a separate "now go find it" step.
    populateModifierTargetSelect(encodeExtraTarget(captured));
    log(`Captured gamepad ${describeBinding(captured)} -- choose what it should do, then Bind action / Record macro.`);
  });

  el.btnModifierListen.addEventListener('click', async () => {
    el.btnModifierListen.disabled = true;
    el.btnModifierListen.textContent = 'Press a button or trigger…';
    const captured = await state.input.startListening({ kind: 'modifier' });
    el.btnModifierListen.disabled = false;
    el.btnModifierListen.textContent = 'Listen…';
    renderModifierPanel();
    if (captured !== null) log(`Modifier button bound to gamepad ${describeBinding(captured)}.`);
  });

  el.btnModifierClear.addEventListener('click', () => {
    state.input.clearModifierBinding();
    renderModifierPanel();
  });

  el.modifierAddForm.addEventListener('submit', (event) => {
    event.preventDefault();
    const target = el.modifierTargetSelect.value;
    if (target === EXTRA_CAPTURE_OPTION_VALUE) {
      log('Choose (or capture) a target first.', true);
      return;
    }
    const action = el.modifierActionSelect.value;
    const extraValue = decodeExtraTarget(target);
    if (extraValue !== null) state.input.setExtraAction(extraValue, action);
    else state.input.setModifiedAction(target, action);
    renderModifierPanel();
    log(`Modifier + ${describeModifierTarget(target)} bound to ${MODIFIABLE_ACTIONS.find((a) => a.name === action)?.label ?? action}.`);
  });

  let recording = false;
  let recordingLiveTimer = null;
  el.btnModifierRecord.addEventListener('click', () => {
    if (!recording) {
      state.input.startRecordingMacro();
      recording = true;
      el.btnModifierRecord.textContent = 'Stop recording';
      el.btnModifierRecord.classList.add('button-active');
      log('Recording macro — perform the button sequence now, then click "Stop recording".');

      el.macroRecordingHint.hidden = false;
      el.macroRecordingLive.hidden = false;
      renderRecordingLive();
      // Polled rather than event-driven: events land on InputManager's own
      // per-source press/release hooks (keyboard/gamepad/touch), and this is
      // just a "here's what's been captured so far" readout, not something
      // that needs to react the instant a press happens.
      recordingLiveTimer = setInterval(renderRecordingLive, 150);
      return;
    }

    clearInterval(recordingLiveTimer);
    recordingLiveTimer = null;
    el.macroRecordingHint.hidden = true;
    el.macroRecordingLive.hidden = true;

    const events = state.input.stopRecordingMacro();
    recording = false;
    el.btnModifierRecord.textContent = 'Record macro';
    el.btnModifierRecord.classList.remove('button-active');

    if (events.length === 0) {
      log('No input captured — macro not saved.', true);
      return;
    }
    const target = el.modifierTargetSelect.value;
    if (target === EXTRA_CAPTURE_OPTION_VALUE) {
      log('Choose (or capture) a target first — macro not saved.', true);
      return;
    }
    const extraValue = decodeExtraTarget(target);
    if (extraValue !== null) state.input.setExtraMacro(extraValue, events);
    else state.input.setModifiedMacro(target, events);
    log(`Modifier + ${describeModifierTarget(target)} bound to a ${events.length}-step macro.`);
    renderModifierPanel();
  });

  el.btnModifierReset.addEventListener('click', () => {
    state.input.resetModifierBindings();
    renderModifierPanel();
    log('Modifier and macro bindings reset.');
  });

  wireMacroBuilder();
  renderModifierPanel();
}

/** Render the in-progress recording's captured events (main.js polls this via setInterval while recording). */
function renderRecordingLive() {
  const events = state.input.getRecordingEvents();
  if (!events) return;

  el.macroRecordingLive.replaceChildren();
  if (events.length === 0) {
    el.macroRecordingLive.append(makeEmptyItem('Nothing captured yet — press a button.'));
    return;
  }
  for (const ev of events) {
    const li = document.createElement('li');
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = `${JOYPAD_ID_TO_NAME[ev.buttonId] ?? `#${ev.buttonId}`} ${ev.pressed ? 'press' : 'release'}`;
    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = `${ev.t}ms`;
    li.append(name, meta);
    el.macroRecordingLive.append(li);
  }
  el.macroRecordingLive.scrollTop = el.macroRecordingLive.scrollHeight;
}

/**
 * Manual macro builder: an alternative to physically performing a sequence,
 * and (via editMacro()) also how an already-saved macro -- built OR recorded
 * -- gets edited afterwards. A "step" is a chord of SNES buttons held
 * together for a duration; steps play back to back with a small fixed gap
 * between them (so a game reliably sees each step as a distinct input rather
 * than one continuous held state). Converts to the exact same
 * {t, buttonId, pressed}[] event shape a real recording produces, so it
 * saves/tests through the identical setModifiedMacro()/playMacro() path --
 * nothing downstream needs to know a macro was built or edited instead of
 * performed.
 */
const MACRO_BUILDER_GAP_MS = 40;
let manualMacroSteps = [];
let manualMacroSelected = new Set();

function buildMacroEventsFromSteps(steps) {
  const events = [];
  let t = 0;
  for (const step of steps) {
    for (const name of step.buttons) events.push({ t, buttonId: JOYPAD[name], pressed: true });
    t += step.holdMs;
    for (const name of step.buttons) events.push({ t, buttonId: JOYPAD[name], pressed: false });
    t += MACRO_BUILDER_GAP_MS;
  }
  return events;
}

/**
 * Inverse of buildMacroEventsFromSteps(), for loading an existing macro
 * (recorded OR previously built) into the editable step list. A step
 * boundary is any change in the currently-held button set -- not just a
 * full release -- so this also round-trips a real recording's overlapping/
 * rolling presses (e.g. pressing B before releasing A) into a sequence of
 * chords rather than only handling the cleanly-separated shape the builder
 * itself produces. The exact original gap between steps is NOT preserved
 * (there is no single "the gap" once holds can overlap); re-saving after
 * editing always re-serialises with the builder's fixed
 * MACRO_BUILDER_GAP_MS, same as a freshly built macro.
 */
function stepsFromMacroEvents(events) {
  const sorted = [...events].sort((a, b) => a.t - b.t);
  const held = new Set();
  const steps = [];
  let stepStartT = 0;

  const pushStep = (buttonIds, endT) => {
    if (buttonIds.size === 0) return;
    const buttons = [...buttonIds].map((id) => JOYPAD_ID_TO_NAME[id]).filter(Boolean);
    if (buttons.length > 0) steps.push({ buttons, holdMs: Math.max(16, endT - stepStartT) });
  };

  let i = 0;
  while (i < sorted.length) {
    const t = sorted[i].t;
    // Every event at this exact timestamp is one atomic transition -- a
    // multi-button chord's presses all land on the same t, and applying them
    // one at a time (rather than as a batch) would split the chord into a
    // spurious zero-duration step the instant the second button "arrives".
    const before = new Set(held);
    while (i < sorted.length && sorted[i].t === t) {
      const ev = sorted[i];
      if (ev.pressed) held.add(ev.buttonId);
      else held.delete(ev.buttonId);
      i++;
    }

    const unchanged = before.size === held.size && [...before].every((id) => held.has(id));
    if (unchanged) continue;

    pushStep(before, t); // close whatever chord was held up to (not including) this timestamp
    stepStartT = t;
  }
  // A recording stopped mid-press has no trailing release event to measure
  // a duration against -- fall back to the builder's own default.
  if (held.size > 0) {
    steps.push({ buttons: [...held].map((id) => JOYPAD_ID_TO_NAME[id]).filter(Boolean), holdMs: 100 });
  }

  return steps;
}

/** Load an existing macro into the builder for editing, in place of performing or building one from scratch. */
/** @param target an SNES button name, or an extra-target encoding (see encodeExtraTarget()) */
function editMacro(target, macroBinding) {
  manualMacroSteps = stepsFromMacroEvents(macroBinding.events);
  manualMacroSelected.clear();
  for (const chip of el.macroStepButtons.children) chip.classList.remove('active');
  populateModifierTargetSelect(target);
  el.macroBuilderDetails.open = true;
  renderMacroSteps();
  el.macroBuilderDetails.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  log(`Editing the ${describeModifierTarget(target)} macro (${manualMacroSteps.length} steps) -- adjust it below, then "Save as macro" to overwrite.`);
}

function wireMacroBuilder() {
  for (const { name, label } of REMAPPABLE_BUTTONS) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'chip';
    chip.textContent = label;
    chip.dataset.button = name;
    chip.addEventListener('click', () => {
      if (manualMacroSelected.has(name)) manualMacroSelected.delete(name);
      else manualMacroSelected.add(name);
      chip.classList.toggle('active', manualMacroSelected.has(name));
    });
    el.macroStepButtons.append(chip);
  }

  el.btnMacroAddStep.addEventListener('click', () => {
    if (manualMacroSelected.size === 0) {
      log('Select at least one button for this step first.', true);
      return;
    }
    const holdMs = Math.max(16, Number(el.macroStepDuration.value) || 100);
    manualMacroSteps.push({ buttons: [...manualMacroSelected], holdMs });

    manualMacroSelected.clear();
    for (const chip of el.macroStepButtons.children) chip.classList.remove('active');

    renderMacroSteps();
  });

  el.btnMacroClearSteps.addEventListener('click', () => {
    manualMacroSteps = [];
    renderMacroSteps();
  });

  el.btnMacroTest.addEventListener('click', () => {
    state.input.playMacro(buildMacroEventsFromSteps(manualMacroSteps));
  });

  el.btnMacroSave.addEventListener('click', () => {
    const target = el.modifierTargetSelect.value;
    if (target === EXTRA_CAPTURE_OPTION_VALUE) {
      log('Choose (or capture) a target first — macro not saved.', true);
      return;
    }
    const events = buildMacroEventsFromSteps(manualMacroSteps);
    const label = `Manual macro (${manualMacroSteps.length} steps)`;
    const extraValue = decodeExtraTarget(target);
    if (extraValue !== null) state.input.setExtraMacro(extraValue, events, label);
    else state.input.setModifiedMacro(target, events, label);
    log(`Modifier + ${describeModifierTarget(target)} bound to a ${manualMacroSteps.length}-step manual macro.`);
    manualMacroSteps = [];
    renderMacroSteps();
    renderModifierPanel();
  });

  renderMacroSteps();
}

function renderMacroSteps() {
  el.macroStepList.replaceChildren();

  if (manualMacroSteps.length === 0) {
    el.macroStepList.append(makeEmptyItem('No steps yet.'));
  } else {
    manualMacroSteps.forEach((step, index) => {
      const li = document.createElement('li');
      li.className = 'macro-step-row';

      const number = document.createElement('span');
      number.className = 'macro-step-number';
      number.textContent = `${index + 1}.`;

      const chord = document.createElement('div');
      chord.className = 'macro-step-chord';
      for (const buttonName of step.buttons) {
        const chip = document.createElement('span');
        chip.className = 'macro-step-chip';
        chip.append(buttonName);

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.textContent = '×';
        removeButton.title = `Remove ${buttonName} from this step`;
        removeButton.addEventListener('click', () => {
          step.buttons = step.buttons.filter((b) => b !== buttonName);
          // A step holding nothing plays back as a no-op -- drop it entirely
          // rather than leave an empty, meaningless row in the list.
          if (step.buttons.length === 0) manualMacroSteps.splice(index, 1);
          renderMacroSteps();
        });
        chip.append(removeButton);
        chord.append(chip);
      }

      const addSelect = document.createElement('select');
      addSelect.className = 'macro-step-add-select';
      addSelect.setAttribute('data-testid', `macro-step-add-${index}`);
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = '+ button';
      addSelect.append(placeholder);
      for (const { name: buttonName, label } of REMAPPABLE_BUTTONS) {
        if (step.buttons.includes(buttonName)) continue;
        const opt = document.createElement('option');
        opt.value = buttonName;
        opt.textContent = label;
        addSelect.append(opt);
      }
      addSelect.addEventListener('change', () => {
        if (!addSelect.value) return;
        step.buttons.push(addSelect.value);
        renderMacroSteps();
      });
      chord.append(addSelect);

      const holdInput = document.createElement('input');
      holdInput.type = 'number';
      holdInput.className = 'macro-step-hold';
      holdInput.min = '16';
      holdInput.max = '10000';
      holdInput.step = '10';
      holdInput.value = String(step.holdMs);
      holdInput.setAttribute('data-testid', `macro-step-hold-${index}`);
      holdInput.addEventListener('change', () => {
        step.holdMs = Math.max(16, Number(holdInput.value) || step.holdMs);
        holdInput.value = String(step.holdMs);
      });

      const holdUnit = document.createElement('span');
      holdUnit.className = 'dim';
      holdUnit.textContent = 'ms';

      const reorder = document.createElement('div');
      reorder.className = 'macro-step-reorder';
      const up = document.createElement('button');
      up.type = 'button';
      up.textContent = '▲';
      up.title = 'Move step earlier';
      up.disabled = index === 0;
      up.addEventListener('click', () => {
        [manualMacroSteps[index - 1], manualMacroSteps[index]] = [manualMacroSteps[index], manualMacroSteps[index - 1]];
        renderMacroSteps();
      });
      const down = document.createElement('button');
      down.type = 'button';
      down.textContent = '▼';
      down.title = 'Move step later';
      down.disabled = index === manualMacroSteps.length - 1;
      down.addEventListener('click', () => {
        [manualMacroSteps[index + 1], manualMacroSteps[index]] = [manualMacroSteps[index], manualMacroSteps[index + 1]];
        renderMacroSteps();
      });
      reorder.append(up, down);

      const remove = document.createElement('button');
      remove.className = 'danger';
      remove.textContent = 'Remove';
      remove.addEventListener('click', () => {
        manualMacroSteps.splice(index, 1);
        renderMacroSteps();
      });

      li.append(number, chord, holdInput, holdUnit, reorder, remove);
      el.macroStepList.append(li);
    });
  }

  const hasSteps = manualMacroSteps.length > 0;
  el.btnMacroTest.disabled = !hasSteps;
  el.btnMacroClearSteps.disabled = !hasSteps;
  el.btnMacroSave.disabled = !hasSteps;
}

/** (Re)build the "Modifier & macros" panel from the input manager's current state. */
/** One row of the modifier combo list, for either an SNES-button target or an extra/spare-button one. `onRemove` does whichever clear call fits the target's kind. */
function buildComboRow(target, binding, onRemove) {
  const li = document.createElement('li');

  const labelText = `Modifier + ${describeModifierTarget(target)}`;
  const descText =
    binding.type === 'action'
      ? `→ ${MODIFIABLE_ACTIONS.find((a) => a.name === binding.action)?.label ?? binding.action}`
      : `→ ${binding.label ?? 'macro'}`;

  // Stacked vertically rather than side-by-side, same fix as the save-state
  // list's thumbnail+date: a long macro label ("Manual macro (N steps)")
  // squeezed the "Modifier + BUTTON" name down to an unreadable
  // ellipsis-truncated sliver once Edit/Remove buttons were also competing
  // for the same row's width. The title attribute is a redundant fallback
  // for the rare case a name is still too long to read in full even on its
  // own line (a very long custom macro label).
  const info = document.createElement('div');
  info.className = 'combo-info';
  info.title = `${labelText} ${descText}`;

  const label = document.createElement('span');
  label.className = 'name';
  label.textContent = labelText;

  const desc = document.createElement('span');
  desc.className = 'meta';
  desc.textContent = descText;

  info.append(label, desc);

  const remove = document.createElement('button');
  remove.className = 'danger';
  remove.textContent = 'Remove';
  remove.addEventListener('click', () => {
    onRemove();
    renderModifierPanel();
  });

  if (binding.type === 'macro') {
    const edit = document.createElement('button');
    edit.textContent = 'Edit';
    edit.addEventListener('click', () => editMacro(target, binding));
    li.append(info, edit, remove);
  } else {
    li.append(info, remove);
  }
  return li;
}

function renderModifierPanel() {
  const modifierValue = state.input.getModifierBinding();
  el.modifierBindingDisplay.textContent = describeBinding(modifierValue) ?? 'unbound';
  el.btnModifierClear.disabled = modifierValue === null || modifierValue === undefined;

  el.modifierComboList.replaceChildren();
  const combos = state.input.getModifiedBindings();
  const extraCombos = state.input.getExtraBindings();
  const names = Object.keys(combos);
  const extraKeys = Object.keys(extraCombos);

  if (names.length === 0 && extraKeys.length === 0) {
    el.modifierComboList.append(makeEmptyItem('No modifier combos configured yet.'));
    return;
  }

  for (const name of names) {
    el.modifierComboList.append(buildComboRow(name, combos[name], () => state.input.clearModifiedBinding(name)));
  }
  for (const key of extraKeys) {
    const value = parseExtraKey(key);
    const target = encodeExtraTarget(value);
    el.modifierComboList.append(buildComboRow(target, extraCombos[key], () => state.input.clearExtraBinding(value)));
  }
}

function handleHotkey(name) {
  switch (name) {
    case 'pause':
      togglePause();
      break;
    case 'reset':
      resetGame();
      break;
    case 'mute':
      toggleMute();
      break;
    case 'fullscreen':
      toggleFullscreen();
      break;
    case 'saveState':
      saveState(currentSlot());
      break;
    case 'loadState':
      loadState(currentSlot());
      break;
    case 'nextSlot':
      el.slotSelect.value = String((currentSlot() % SAVE_STATE_SLOT_COUNT) + 1);
      onSlotChanged();
      refreshSaveList();
      break;
    case 'prevSlot':
      el.slotSelect.value = String(currentSlot() === 1 ? SAVE_STATE_SLOT_COUNT : currentSlot() - 1);
      onSlotChanged();
      refreshSaveList();
      break;
    case 'slot1':
    case 'slot2':
    case 'slot3':
    case 'slot4':
      el.slotSelect.value = name.slice(4);
      onSlotChanged();
      refreshSaveList();
      break;
    default:
      break;
  }
}

/* -------------------------------------------------------------- loading -- */

async function loadRomFile(file) {
  try {
    log(`Reading ${file.name}…`);
    const bytes = new Uint8Array(await file.arrayBuffer());
    const record = await storage.saveRom(file.name, bytes);
    await startRom(record.id, record.name, bytes, record.lastSlot);
    await refreshRomList();
    if (state.syncSession) sync.pushDirtyRoms().catch((err) => setSyncStatus(`Sync failed: ${err?.message ?? err}`, true));
  } catch (err) {
    log(`Could not load ${file.name}: ${err?.message ?? err}`, true);
  }
}

async function startRomFromStorage(id) {
  const record = await storage.getRom(id);
  if (!record) {
    log('That ROM is no longer in local storage.', true);
    await refreshRomList();
    return;
  }
  await storage.touchRom(id);
  await startRom(record.id, record.name, new Uint8Array(record.data), record.lastSlot);
  await refreshRomList();
}

async function startRom(id, name, bytes, lastSlot) {
  stopLoop();
  setPausedOverlay(false); // a paused overlay from a previous game must not survive switching ROMs

  if (!state.core.loadRom(bytes)) {
    log(`The core rejected ${name}. Is it a valid SNES ROM?`, true);
    appStatus.romLoaded = false;
    return;
  }

  state.currentGame = { id, name };
  state.gameEditor = detectEditor(state.core);
  renderGameEditor();
  appStatus.romLoaded = true;
  appStatus.gameName = name;
  appStatus.frames = 0;

  // Restore whichever slot was last used for this game (1 if it's never
  // been saved before) -- onSlotChanged() re-persists the same value here,
  // which is a harmless no-op, not a fresh "change", so it shouldn't
  // announce a toast right as the game is starting.
  el.slotSelect.value = String(lastSlot && lastSlot >= 1 ? lastSlot : 1);
  onSlotChanged({ announce: false });

  resizeCanvas();
  el.dropHint.hidden = true;

  for (const b of [el.btnPause, el.btnReset, el.btnSaveState, el.btnLoadState]) {
    b.disabled = false;
  }

  // The AudioContext can only start from a user gesture; loading a ROM is
  // always the result of one (file pick, drop, or a library click).
  state.audio.start(state.core.sampleRate);
  await state.audio.resume();

  await refreshSaveList();
  await refreshCheatList();
  if (state.syncSession) runSync({ silent: true });

  log(`Running ${name} (${state.core.width}x${state.core.height} @ ${state.core.fps.toFixed(2)}Hz)`);
  startLoop();
}

/* ----------------------------------------------------------------- loop -- */

function resizeCanvas() {
  const w = state.core.width;
  const h = state.core.height;
  if (offscreen.width !== w || offscreen.height !== h) {
    offscreen.width = w;
    offscreen.height = h;
    state.imageData = null;
  }
}

/**
 * Blit the core's framebuffer to the offscreen (native-resolution) canvas,
 * then hand off to presentFrame() to project it onto the visible one.
 *
 * The core's buffer is a fixed 1024-pixel stride regardless of the active
 * resolution, so when the SNES is in a narrower mode we cannot hand the whole
 * buffer to putImageData -- we copy row by row into a correctly-sized
 * ImageData. At 224 rows this is not measurable next to emulation itself.
 */
function blit() {
  const fb = state.core.framebuffer();

  if (offscreen.width !== fb.width || offscreen.height !== fb.height) {
    offscreen.width = fb.width;
    offscreen.height = fb.height;
    state.imageData = null;
  }

  if (!state.imageData || state.imageData.width !== fb.width) {
    state.imageData = offscreenCtx.createImageData(fb.width, fb.height);
  }

  const dst = state.imageData.data;
  const rowBytes = fb.width * 4;
  const strideBytes = fb.stride * 4;

  for (let y = 0; y < fb.height; y++) {
    const srcStart = y * strideBytes;
    dst.set(fb.pixels.subarray(srcStart, srcStart + rowBytes), y * rowBytes);
  }

  offscreenCtx.putImageData(state.imageData, 0, 0);
  presentFrame(fb.width, fb.height);
}

/**
 * Project the native-resolution offscreen frame onto the visible canvas.
 *
 * Two independent presentation paths:
 *  - Default (2D canvas, presentFrame2D): pixelated / smooth / sharp-bilinear,
 *    always available, always what a fresh checkout renders with.
 *  - Advanced video (WebGL, gl-filters.js): real edge-directed upscaling
 *    shaders (xBR/xBRZ/Sabr/ScaleFX). Strictly opt-in via the "Advanced
 *    video" toggle; if it's off, or the selected shader isn't available
 *    (WebGL2/float-framebuffer support, or a compile failure) this falls
 *    straight back to the 2D path with a logged reason -- never a blank
 *    screen or a silently wrong filter.
 */
function presentFrame(w, h) {
  const useGl = state.advancedVideo && state.glRenderer?.isFilterReady(state.advancedFilter);

  if (useGl) {
    try {
      const rect = el.stage.getBoundingClientRect();
      state.glRenderer.render(offscreen, state.advancedFilter, w, h, rect.width, rect.height);
      el.canvasGl.style.imageRendering = 'auto';
      el.canvas.hidden = true;
      el.canvasGl.hidden = false;
      return;
    } catch (err) {
      log(`Advanced video filter failed (${err?.message ?? err}); falling back to standard video.`, true);
    }
  }

  el.canvas.hidden = false;
  el.canvasGl.hidden = true;
  presentFrame2D(w, h);
}

/**
 * The default presentation path: pixelated / smooth / sharp-bilinear.
 *
 *  - pixelated / smooth: the visible canvas stays at native resolution and
 *    CSS `image-rendering` does the upscaling (nearest-neighbour vs.
 *    browser bilinear).
 *  - sharp-bilinear: the canvas itself is pre-scaled by the largest integer
 *    factor that fits the stage, with nearest-neighbour sampling (crisp
 *    pixel edges, no shimmer), and CSS is left to smooth only the small
 *    leftover fractional stretch to the container -- the standard
 *    "sharp-bilinear" trick emulators use to upscale pixel art cleanly.
 */
function presentFrame2D(w, h) {
  if (state.videoFilter === 'sharp-bilinear') {
    const rect = el.stage.getBoundingClientRect();
    const rawScale = Math.min(rect.width / w, rect.height / h) || 1;
    const scale = Math.max(1, Math.floor(rawScale));
    const tw = w * scale;
    const th = h * scale;

    if (el.canvas.width !== tw || el.canvas.height !== th) {
      el.canvas.width = tw;
      el.canvas.height = th;
    }
    ctx2d.imageSmoothingEnabled = false;
    ctx2d.drawImage(offscreen, 0, 0, tw, th);
    el.canvas.style.imageRendering = 'auto';
  } else {
    if (el.canvas.width !== w || el.canvas.height !== h) {
      el.canvas.width = w;
      el.canvas.height = h;
    }
    ctx2d.drawImage(offscreen, 0, 0);
    el.canvas.style.imageRendering = state.videoFilter === 'smooth' ? 'auto' : 'pixelated';
  }
}

function startLoop() {
  if (state.running) return;
  state.running = true;
  el.btnPause.textContent = 'Pause';
  state.lastFrameTime = performance.now();
  state.frameAccumulator = 0;
  state.fpsCounter = { frames: 0, since: performance.now() };
  state.rafId = requestAnimationFrame(tick);
}

function stopLoop() {
  state.running = false;
  if (state.rafId !== null) {
    cancelAnimationFrame(state.rafId);
    state.rafId = null;
  }
  el.btnPause.textContent = 'Resume';
}

/**
 * One animation frame.
 *
 * The SNES runs at ~60.098Hz and displays refresh at 60Hz (or 120, or 144),
 * so we accumulate elapsed real time and emit as many emulated frames as it
 * has paid for, rather than assuming one emulated frame per rAF tick. The
 * accumulator is clamped to a few frames: after a long stall (tab restore, a
 * blocking IndexedDB write) catching up fully would freeze the page, so we
 * drop the missed time instead.
 *
 * Fast forward works by scaling *time* rather than uncapping the loop: the
 * accumulator is fed elapsed-time * FAST_FORWARD_MULTIPLIER, so the same
 * frame-pacing logic just thinks more time passed and runs proportionally
 * more emulated frames this tick. Audio defaults to muted for the duration
 * (see AudioOutput.setFastForward), but the user can opt into pitched-up
 * "condensed" audio instead via the "Sound during fast-forward" checkbox
 * (state.audioDuringFastForward / AudioOutput.setAudioDuringFastForward) --
 * played back at the same FAST_FORWARD_MULTIPLIER rate audio.js schedules
 * buffers with, matching the rate the core is actually producing frames at.
 */
function tick(now) {
  if (!state.running) return;

  const fastForward = state.input.isActionHeld('fastForward');
  if (fastForward !== state.fastForward) {
    state.fastForward = fastForward;
    state.audio.setFastForward(fastForward, FAST_FORWARD_MULTIPLIER);
    el.stage.classList.toggle('fast-forward', fastForward);
  }
  const speedMultiplier = state.fastForward ? FAST_FORWARD_MULTIPLIER : 1;

  const frameDuration = 1000 / (state.core.fps || 60);
  let elapsed = now - state.lastFrameTime;
  state.lastFrameTime = now;

  if (elapsed > 250) elapsed = frameDuration; // long stall: don't try to catch up
  state.frameAccumulator += elapsed * speedMultiplier;

  const maxFrames = BASE_MAX_FRAMES_PER_TICK * speedMultiplier;
  let framesThisTick = 0;

  while (state.frameAccumulator >= frameDuration && framesThisTick < maxFrames) {
    // Gamepad polling happens on its own always-running loop (see boot()),
    // not here -- it needs to keep working while paused.
    state.core.runFrame();
    state.audio.pump(state.core);
    state.frameAccumulator -= frameDuration;
    framesThisTick++;
    appStatus.frames++;
  }

  if (state.frameAccumulator > frameDuration * maxFrames) {
    state.frameAccumulator = 0;
  }

  if (framesThisTick > 0) blit();

  // FPS readout, updated twice a second.
  state.fpsCounter.frames += framesThisTick;
  const span = now - state.fpsCounter.since;
  if (span >= 500) {
    const fps = (state.fpsCounter.frames * 1000) / span;
    el.fpsBadge.textContent = `${fps.toFixed(1)} fps`;
    state.fpsCounter = { frames: 0, since: now };
  }

  state.rafId = requestAnimationFrame(tick);
}

/* -------------------------------------------------------------- actions -- */

function togglePause() {
  if (!appStatus.romLoaded) return;
  if (state.running) {
    stopLoop();
    log('Paused.');
    showToast('Paused');
    setPausedOverlay(true);
  } else {
    state.audio.resume();
    startLoop();
    log('Resumed.');
    showToast('Resumed');
    setPausedOverlay(false);
  }
}

function resetGame() {
  if (!appStatus.romLoaded) return;
  state.core.reset();
  log('Reset.');
}

function toggleMute() {
  const muted = !state.audio.muted;
  state.audio.setMuted(muted);
  el.btnMute.textContent = muted ? 'Unmute' : 'Mute';
}

const THUMBNAIL_WIDTH = 128;

/**
 * A small PNG data URL of the current frame, for the save-state list.
 * Always captured from `offscreen` (the native-resolution 2D canvas `blit()`
 * draws every frame into) rather than whichever canvas is currently visible
 * (`#screen` or `#screen-gl`) -- offscreen is always current regardless of
 * which video presentation path is active, and is a plain 2D canvas, so
 * there's no WebGL-readback complexity to worry about.
 */
function captureThumbnail() {
  if (offscreen.width === 0 || offscreen.height === 0) return null;
  const scale = THUMBNAIL_WIDTH / offscreen.width;
  const thumb = document.createElement('canvas');
  thumb.width = THUMBNAIL_WIDTH;
  thumb.height = Math.round(offscreen.height * scale);
  const tctx = thumb.getContext('2d', { alpha: false });
  tctx.drawImage(offscreen, 0, 0, thumb.width, thumb.height);
  return thumb.toDataURL('image/png');
}

async function saveState(slot) {
  if (!appStatus.romLoaded || !state.currentGame) return;
  try {
    const data = state.core.serialize();
    if (!data) {
      log('The core produced no save state.', true);
      return;
    }
    await storage.putSaveState(state.currentGame.id, slot, data, {
      gameName: state.currentGame.name,
      thumbnail: captureThumbnail(),
    });
    log(`Saved to slot ${slot} (${(data.length / 1024).toFixed(0)} KB).`);
    showToast(`Saved · slot ${slot}`);
    await refreshSaveList();
    if (state.syncSession) sync.pushDirtySaves().catch((err) => setSyncStatus(`Sync failed: ${err?.message ?? err}`, true));
  } catch (err) {
    log(`Save failed: ${err?.message ?? err}`, true);
  }
}

async function loadState(slot) {
  if (!appStatus.romLoaded || !state.currentGame) return;
  try {
    const record = await storage.getSaveState(state.currentGame.id, slot);
    if (!record) {
      log(`Slot ${slot} is empty.`);
      return;
    }
    if (state.core.unserialize(new Uint8Array(record.data))) {
      // Buffered audio belongs to the pre-load timeline; playing it after a
      // state load is heard as a glitch.
      state.core.flushAudio();
      // Loading a specific slot (e.g. clicking "Load" on a save-list row for
      // a slot other than the currently-selected one) should make it current
      // -- otherwise the next Save would silently overwrite a different slot
      // than the one just loaded.
      if (el.slotSelect.value !== String(slot)) {
        el.slotSelect.value = String(slot);
        onSlotChanged({ announce: false });
      }
      log(`Loaded slot ${slot}.`);
      showToast(`Loaded · slot ${slot}`);
    } else {
      log(`Slot ${slot} could not be restored (state/ROM mismatch?).`, true);
    }
  } catch (err) {
    log(`Load failed: ${err?.message ?? err}`, true);
  }
}

/* --------------------------------------------------------------- lists -- */

function formatBytes(n) {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/**
 * The library list merges local ROMs with "cloud-only" entries -- ROMs
 * synced (metadata only, see sync.js's module doc) from another device but
 * never downloaded to this one. A cloud-only entry gets a "Download" button
 * instead of Play/Delete; clicking it fetches the actual bytes on demand
 * (sync.downloadRom()), writes them into local IndexedDB, and starts
 * playing -- ROMs are multi-MB, so nothing about this list ever downloads
 * one until a user explicitly asks for it.
 */
async function refreshRomList() {
  const localRoms = await storage.listRoms();

  let cloudOnly = [];
  if (state.syncSession) {
    try {
      const localIds = new Set(localRoms.map((r) => r.id));
      cloudOnly = (await sync.listRemoteRoms())
        .filter((row) => !localIds.has(row.rom_id))
        .map((row) => ({ id: row.rom_id, name: row.name, size: row.size, cloudOnly: true, remoteRow: row }));
    } catch (err) {
      console.warn('[snes9x-web] could not list cloud ROMs:', err?.message ?? err);
    }
  }

  el.romList.replaceChildren();

  const entries = [...localRoms, ...cloudOnly];
  if (entries.length === 0) {
    el.romList.append(makeEmptyItem('No ROMs stored yet.'));
    return;
  }

  for (const rom of entries) {
    const li = document.createElement('li');

    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = rom.name;
    name.title = rom.cloudOnly ? `${rom.name}\n${rom.id}\n(not downloaded to this device)` : `${rom.name}\n${rom.id}`;

    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = rom.cloudOnly ? `${formatBytes(rom.size)} · cloud` : formatBytes(rom.size);

    if (rom.cloudOnly) {
      const download = document.createElement('button');
      download.textContent = 'Download';
      download.addEventListener('click', async () => {
        download.disabled = true;
        download.textContent = 'Downloading…';
        try {
          const record = await sync.downloadRom(rom.remoteRow);
          log(`Downloaded ${record.name} (${formatBytes(record.size)}).`);
          await startRom(record.id, record.name, new Uint8Array(record.data), record.lastSlot);
          await refreshRomList();
        } catch (err) {
          log(`Download failed: ${err?.message ?? err}`, true);
          download.disabled = false;
          download.textContent = 'Download';
        }
      });
      li.append(name, meta, download);
    } else {
      const play = document.createElement('button');
      play.textContent = 'Play';
      play.addEventListener('click', () => startRomFromStorage(rom.id));

      const del = document.createElement('button');
      del.className = 'danger';
      del.textContent = 'Delete';
      del.addEventListener('click', async () => {
        await storage.deleteRom(rom.id);
        if (state.currentGame?.id === rom.id) {
          stopLoop();
          setPausedOverlay(false);
          state.core.unloadRom();
          state.currentGame = null;
          state.gameEditor = null;
          renderGameEditor();
          appStatus.romLoaded = false;
          el.dropHint.hidden = false;
        }
        await refreshRomList();
        await refreshSaveList();
        await refreshCheatList();
      });

      li.append(name, meta, play, del);
    }

    el.romList.append(li);
  }
}

/*
 * Save state export/import: a manual, file-based way to back up or move a
 * save between browsers/devices, independent of (and complementary to)
 * cloud sync -- useful with no Supabase project configured at all, or just
 * as a local backup before trying something risky in-game.
 *
 * Exported as JSON (not the raw serialize blob directly) so a single file
 * can carry the state's metadata -- game name, slot, timestamps, thumbnail
 * -- alongside the bytes, self-describing enough to show something
 * meaningful if it's ever opened outside this app, and enough to validate
 * against on import. The raw state bytes are base64-encoded to fit inside
 * JSON's string type.
 *
 * A single-slot export/import (SAVE_EXPORT_FORMAT) targets whatever slot is
 * currently selected in the dropdown -- the same slot "Save state"/"Load
 * state" already operate on, so there's exactly one place that decides
 * "which slot" during an import. A bundle (SAVE_BUNDLE_FORMAT, "Download
 * all") carries its own slot numbers per entry instead, since restoring a
 * whole backup is about recreating the original layout, not funneling
 * everything into one slot.
 */
const SAVE_EXPORT_FORMAT = 'snes9x-web-save-state';
const SAVE_BUNDLE_FORMAT = 'snes9x-web-save-bundle';
const SAVE_EXPORT_VERSION = 1;

function sanitizeFilename(name) {
  return name.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'save';
}

/** Chunked to avoid blowing the call stack on String.fromCharCode(...bytes) for a large (multi-hundred-KB) save state. */
function bufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Export one save state (with its full data, not just metadata) as a downloadable .snes9xstate JSON file. */
async function exportSaveState(save) {
  const full = await storage.getSaveState(save.gameId, save.slot);
  if (!full) {
    log('Could not export -- that save no longer exists.', true);
    return;
  }
  const envelope = {
    format: SAVE_EXPORT_FORMAT,
    version: SAVE_EXPORT_VERSION,
    gameId: full.gameId,
    gameName: full.gameName,
    slot: full.slot,
    createdAt: full.createdAt,
    updatedAt: full.updatedAt,
    size: full.size,
    thumbnail: full.thumbnail ?? null,
    data: bufferToBase64(full.data),
  };
  const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
  downloadBlob(blob, `${sanitizeFilename(full.gameName ?? 'save')}-slot${full.slot}.snes9xstate`);
  log(`Exported slot ${full.slot} (${formatBytes(full.size)}).`);
}

/** Export every save state for the current game as one downloadable .snes9xstates bundle. */
async function exportAllSaves() {
  if (!state.currentGame) return;
  const saves = await storage.listSaves(state.currentGame.id);
  if (saves.length === 0) {
    log('No save states to export.', true);
    return;
  }

  const entries = [];
  for (const meta of saves) {
    const full = await storage.getSaveState(meta.gameId, meta.slot);
    if (!full) continue;
    entries.push({
      slot: full.slot,
      createdAt: full.createdAt,
      updatedAt: full.updatedAt,
      size: full.size,
      thumbnail: full.thumbnail ?? null,
      data: bufferToBase64(full.data),
    });
  }

  const bundle = {
    format: SAVE_BUNDLE_FORMAT,
    version: SAVE_EXPORT_VERSION,
    gameId: state.currentGame.id,
    gameName: state.currentGame.name,
    exportedAt: Date.now(),
    saves: entries,
  };
  const blob = new Blob([JSON.stringify(bundle)], { type: 'application/json' });
  downloadBlob(blob, `${sanitizeFilename(state.currentGame.name)}-all-slots.snes9xstates`);
  const totalBytes = entries.reduce((sum, e) => sum + e.size, 0);
  log(`Exported ${entries.length} save state${entries.length === 1 ? '' : 's'} (${formatBytes(totalBytes)} total).`);
}

/**
 * Delete every save state for the current game, locally and (if signed in)
 * from Supabase too -- a local-only delete would just get silently undone
 * by the next sync, since pullRemoteSaves() has no way to distinguish "the
 * user deleted this" from "this device hasn't seen it yet". Called only
 * from the confirm-dialog flow in wireUi(), never directly from the trigger
 * button.
 */
async function deleteAllSaveStates() {
  if (!state.currentGame) return;
  const gameId = state.currentGame.id;
  const slots = await storage.deleteAllSaves(gameId);
  if (slots.length === 0) return;

  if (state.syncSession) {
    try {
      const { deletedRemote } = await sync.deleteAllRemoteSaves(gameId, slots);
      log(`Deleted all ${slots.length} save state${slots.length === 1 ? '' : 's'} (${deletedRemote} synced ${deletedRemote === 1 ? 'copy' : 'copies'} removed too).`);
    } catch (err) {
      log(
        `Deleted all ${slots.length} local save state${slots.length === 1 ? '' : 's'}, but removing the synced copies failed: ${err?.message ?? err}. They may reappear on your next sync.`,
        true,
      );
    }
  } else {
    log(`Deleted all ${slots.length} save state${slots.length === 1 ? '' : 's'}.`);
  }

  await refreshSaveList();
}

/**
 * A save state is tied to specific emulated RAM layout, so importing one
 * from a different game is very likely to misbehave if loaded -- worth a
 * warning, but not worth blocking (the user may know something we don't,
 * e.g. two ROM releases of the same game under different names). Returns a
 * suffix to fold into the SAME log() call as the outcome, rather than a
 * separate log() of its own: log() overwrites a single status line, so a
 * warning logged before the "Imported..." message that follows it right
 * after would just be invisibly clobbered.
 */
function gameMismatchWarning(fileGameName) {
  if (fileGameName && state.currentGame && fileGameName !== state.currentGame.name) {
    return ` Warning: this file was saved from "${fileGameName}", not "${state.currentGame.name}" -- loading it probably won't work.`;
  }
  return '';
}

/**
 * Show the "this will overwrite N existing save states" dialog and resolve
 * with the user's choice. Only called when there's an actual conflict --
 * importing into slots that are all currently empty just proceeds
 * immediately with no prompt, since there's nothing at risk to warn about.
 * @returns {Promise<'overwrite'|'append'|null>} null means Cancel (or the dialog was dismissed, e.g. Esc)
 */
function confirmImportChoice(message) {
  return new Promise((resolve) => {
    el.importConflictMessage.textContent = message;
    el.importConflictDialog.showModal();

    const cleanup = () => {
      el.btnImportOverwrite.removeEventListener('click', onOverwrite);
      el.btnImportAppend.removeEventListener('click', onAppend);
      el.btnImportCancel.removeEventListener('click', onCancel);
      el.importConflictDialog.removeEventListener('cancel', onDialogCancel);
    };
    const settle = (value) => {
      cleanup();
      el.importConflictDialog.close();
      resolve(value);
    };
    const onOverwrite = () => settle('overwrite');
    const onAppend = () => settle('append');
    const onCancel = () => settle(null);
    // The dialog's native 'cancel' event (Esc key) closes it on its own --
    // don't call .close() again in that case, just clean up and resolve.
    const onDialogCancel = () => {
      cleanup();
      resolve(null);
    };

    el.btnImportOverwrite.addEventListener('click', onOverwrite);
    el.btnImportAppend.addEventListener('click', onAppend);
    el.btnImportCancel.addEventListener('click', onCancel);
    el.importConflictDialog.addEventListener('cancel', onDialogCancel);
  });
}

/** Lowest slot (1..SAVE_STATE_SLOT_COUNT) that's neither already occupied nor already claimed earlier in the same import, or null if every slot is full. */
function findFreeSlot(occupiedSlots, claimedSlots) {
  for (let slot = 1; slot <= SAVE_STATE_SLOT_COUNT; slot++) {
    if (!occupiedSlots.has(slot) && !claimedSlots.has(slot)) return slot;
  }
  return null;
}

async function importSaveFile(file) {
  if (!state.currentGame) {
    log('Load a game before importing a save state.', true);
    return;
  }

  let parsed;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    log(`"${file.name}" is not a valid save file.`, true);
    return;
  }

  const existingSaves = await storage.listSaves(state.currentGame.id);
  const occupiedSlots = new Set(existingSaves.map((s) => s.slot));

  if (parsed.format === SAVE_EXPORT_FORMAT && typeof parsed.data === 'string') {
    const warning = gameMismatchWarning(parsed.gameName);
    let targetSlot = currentSlot();

    if (occupiedSlots.has(targetSlot)) {
      const choice = await confirmImportChoice(
        `Slot ${targetSlot} already has a save state for "${state.currentGame.name}". Importing "${file.name}" will overwrite it.`,
      );
      if (choice === null) {
        log('Import cancelled.');
        return;
      }
      if (choice === 'append') {
        const freeSlot = findFreeSlot(occupiedSlots, new Set());
        if (freeSlot === null) {
          log('Every save slot is full -- could not append.', true);
          return;
        }
        targetSlot = freeSlot;
      }
    }

    const data = base64ToBuffer(parsed.data);
    await storage.putSaveState(state.currentGame.id, targetSlot, data, {
      createdAt: parsed.createdAt,
      gameName: state.currentGame.name,
      thumbnail: parsed.thumbnail ?? null,
    });
    // If it landed somewhere other than the slot that was selected when the
    // import started (append picked a different one), reflect that in the
    // dropdown -- otherwise it would silently point at the wrong slot.
    if (String(targetSlot) !== el.slotSelect.value) {
      el.slotSelect.value = String(targetSlot);
      onSlotChanged({ announce: false });
    }
    log(`Imported "${file.name}" into slot ${targetSlot} (${formatBytes(data.byteLength)}).${warning}`, Boolean(warning));
    if (state.syncSession) sync.pushDirtySaves().catch((err) => setSyncStatus(`Sync failed: ${err?.message ?? err}`, true));
  } else if (parsed.format === SAVE_BUNDLE_FORMAT && Array.isArray(parsed.saves)) {
    const warning = gameMismatchWarning(parsed.gameName);
    const validEntries = parsed.saves.filter(
      (entry) => Number.isInteger(entry.slot) && entry.slot >= 1 && entry.slot <= SAVE_STATE_SLOT_COUNT && typeof entry.data === 'string',
    );
    const conflictingSlots = validEntries.filter((e) => occupiedSlots.has(e.slot)).map((e) => e.slot);

    let mode = 'overwrite';
    if (conflictingSlots.length > 0) {
      const choice = await confirmImportChoice(
        `Importing "${file.name}" will overwrite ${conflictingSlots.length} existing save state${conflictingSlots.length === 1 ? '' : 's'} in slot${conflictingSlots.length === 1 ? '' : 's'} ${conflictingSlots.join(', ')} for "${state.currentGame.name}".`,
      );
      if (choice === null) {
        log('Import cancelled.');
        return;
      }
      mode = choice;
    }

    const claimedSlots = new Set();
    const importedSlots = [];
    for (const entry of validEntries) {
      let targetSlot = entry.slot;
      // Re-check against claimedSlots too, not just occupiedSlots: an
      // earlier entry in this SAME import may have just been appended into
      // the slot this entry was originally recorded at.
      if (mode === 'append' && (occupiedSlots.has(targetSlot) || claimedSlots.has(targetSlot))) {
        const freeSlot = findFreeSlot(occupiedSlots, claimedSlots);
        if (freeSlot === null) {
          log(`Every save slot is full -- stopped after importing ${importedSlots.length} of ${validEntries.length}.`, true);
          break;
        }
        targetSlot = freeSlot;
      }
      claimedSlots.add(targetSlot);

      const data = base64ToBuffer(entry.data);
      await storage.putSaveState(state.currentGame.id, targetSlot, data, {
        createdAt: entry.createdAt,
        gameName: state.currentGame.name,
        thumbnail: entry.thumbnail ?? null,
      });
      importedSlots.push(targetSlot);
    }

    log(
      `Imported ${importedSlots.length} save state${importedSlots.length === 1 ? '' : 's'} from "${file.name}" (slots: ${importedSlots.join(', ') || 'none'}).${warning}`,
      Boolean(warning),
    );
    if (importedSlots.length > 0 && state.syncSession) {
      sync.pushDirtySaves().catch((err) => setSyncStatus(`Sync failed: ${err?.message ?? err}`, true));
    }
  } else {
    log(`"${file.name}" doesn't look like a snes9x-web save file.`, true);
    return;
  }

  await refreshSaveList();
}

async function refreshSaveList() {
  el.saveList.replaceChildren();

  if (!state.currentGame) {
    el.saveList.append(makeEmptyItem('Load a game to see its save states.'));
    el.btnSaveDownloadAll.disabled = true;
    el.btnSaveDeleteAll.disabled = true;
    el.saveTotalSize.textContent = '';
    return;
  }

  const saves = await storage.listSaves(state.currentGame.id);
  el.btnSaveDownloadAll.disabled = saves.length === 0;
  el.btnSaveDeleteAll.disabled = saves.length === 0;
  if (saves.length === 0) {
    el.saveList.append(makeEmptyItem('No save states for this game.'));
    el.saveTotalSize.textContent = '';
    return;
  }

  const totalBytes = saves.reduce((sum, save) => sum + save.size, 0);
  el.saveTotalSize.textContent = `(${saves.length} slot${saves.length === 1 ? '' : 's'}, ${formatBytes(totalBytes)} total)`;

  for (const save of saves) {
    const li = document.createElement('li');

    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'save-thumb-wrap';

    const thumb = document.createElement('img');
    thumb.className = 'save-thumb';
    thumb.alt = `Slot ${save.slot} thumbnail`;
    thumb.width = 48;
    thumb.height = 42;
    if (save.thumbnail) thumb.src = save.thumbnail;
    else thumb.classList.add('save-thumb-empty');

    const slotBadge = document.createElement('span');
    slotBadge.className = 'save-thumb-slot';
    slotBadge.textContent = save.slot;

    thumbWrap.append(thumb, slotBadge);

    // Stacked rather than side-by-side: with a thumbnail, date, and two
    // buttons all in one row, a side-by-side name+meta was getting squeezed
    // to 0 width in a narrow panel column -- confirmed via
    // getBoundingClientRect(), not just a hunch.
    const info = document.createElement('div');
    info.className = 'save-info';

    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = `Slot ${save.slot}`;

    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = `${new Date(save.updatedAt).toLocaleString()} · ${formatBytes(save.size)}`;

    info.append(name, meta);

    const load = document.createElement('button');
    load.textContent = 'Load';
    load.addEventListener('click', () => loadState(save.slot));

    const exportBtn = document.createElement('button');
    exportBtn.textContent = 'Export';
    exportBtn.addEventListener('click', () => exportSaveState(save));

    const del = document.createElement('button');
    del.className = 'danger';
    del.textContent = 'Delete';
    del.addEventListener('click', async () => {
      await storage.deleteSave(save.id);
      await refreshSaveList();
    });

    li.append(thumbWrap, info, load, exportBtn, del);
    el.saveList.append(li);
  }
}

/* --------------------------------------------------------------- cheats -- */

/** One-time setup for the "Cheats" panel: wire the add-form, then render whatever's already there (nothing, before a ROM loads). */
function wireCheats() {
  el.cheatAddForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.currentGame) return;

    const code = normalizeCheatCode(el.cheatCodeInput.value);
    if (!isValidCheatCode(code)) {
      log(`"${el.cheatCodeInput.value.trim()}" doesn't look like a Game Genie (XXXX-XXXX) or Pro Action Replay (8 hex digits) code.`, true);
      return;
    }

    const description = el.cheatDescInput.value.trim();
    await storage.addCheat(state.currentGame.id, code, description);
    el.cheatCodeInput.value = '';
    el.cheatDescInput.value = '';
    el.cheatCodeInput.focus();

    log(`Added cheat${description ? ` "${description}"` : ''} (${code}).`);
    await refreshCheatList();
    pushCheatsIfSignedIn();
  });

  renderCheatList();
}

/**
 * One-click codes supplied by the active game-editor plugin, so a user
 * doesn't have to find or derive a code by hand. Hidden for any ROM whose
 * plugin offers none (and for no plugin at all).
 *
 * Two deliberate differences from a hand-typed cheat: a preset is added
 * ENABLED (clicking a named button is an explicit "turn this on", whereas
 * the manual form's disabled-by-default is easy to miss and caused several
 * "this code does nothing" false alarms during development), and its
 * storage id is derived from the preset + game rather than random, so
 * clicking the same preset twice updates one record instead of stacking
 * duplicates.
 */
function renderCheatPresets() {
  const presets = state.gameEditor?.presetCheats;
  const enabled = Boolean(state.currentGame && presets?.length);
  el.cheatPresets.hidden = !enabled;
  if (!enabled) return;

  const active = new Set(state.cheats.map((c) => normalizeCheatCode(c.code)));
  el.cheatPresetList.replaceChildren();
  for (const preset of presets) {
    const li = document.createElement('li');

    const info = document.createElement('span');
    info.className = 'combo-info';
    info.title = `${preset.description} (${preset.code})`;
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = preset.name;
    const desc = document.createElement('span');
    desc.className = 'dim';
    desc.textContent = preset.description;
    info.append(name, desc);
    li.append(info);

    const already = active.has(normalizeCheatCode(preset.code));
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'button';
    add.textContent = already ? 'Added' : 'Add';
    add.disabled = already;
    add.setAttribute('data-testid', `cheat-preset-add-${preset.id}`);
    add.addEventListener('click', async () => {
      const gameId = state.currentGame?.id;
      if (!gameId) return;
      const id = await storage.deterministicUuid(`preset-${gameId}-${preset.id}`);
      await storage.putCheat(id, gameId, preset.code, preset.name, true);
      log(`Added and enabled "${preset.name}" (${preset.code}).`);
      await refreshCheatList();
      pushCheatsIfSignedIn();
    });
    li.append(add);
    el.cheatPresetList.append(li);
  }
}

/**
 * Registers state.cheats with the core's cheat engine (applyCheats(), which
 * only takes effect for real starting the NEXT frame the core runs), then
 * immediately primes anything that needs to be readable before that frame
 * happens -- so far just the Item Editor's own cheats, whose values
 * `plugin.readItemBonus()` reads back out of `core.rom()` synchronously the
 * moment a user opens the panel. Without the prime step, re-opening the
 * Item Editor right after a ROM (re)load could show an item's default
 * value instead of whatever a stored cheat set it to, purely because no
 * frame had run yet to let the cheat engine's own per-frame pass catch up.
 * Confirmed as a real gap (not theoretical) before this fix existed.
 */
function applyAndPrimeCheats() {
  applyCheats(state.core, state.cheats);
  state.gameEditor?.primeCheats?.(state.core, state.cheats);
}

/** Reload state.cheats from storage for the current game, push the enabled ones to the core, and re-render. */
async function refreshCheatList() {
  const gameId = state.currentGame?.id ?? null;
  const loaded = gameId ? await storage.listCheats(gameId) : [];

  // A real bug briefly gave the Item Editor's auto-generated cheats a
  // plain, human-readable id instead of a UUID; the `cheats` table's id
  // column is a Postgres uuid, so those could never sync and would fail
  // every subsequent "Sync now" with "invalid input syntax for type uuid".
  // Purge any still lurking locally -- safe to just delete, since
  // re-editing the same item/field in the Item Editor recreates an
  // equivalent cheat with a proper id from scratch.
  const invalid = loaded.filter((c) => !storage.isValidUuid(c.id));
  if (invalid.length > 0) {
    await Promise.all(invalid.map((c) => storage.deleteCheat(c.id)));
    log(
      `Removed ${invalid.length} old cheat${invalid.length === 1 ? '' : 's'} that could never sync (invalid id) -- re-apply from the Item Editor if you still want it.`,
      true,
    );
  }

  state.cheats = loaded.filter((c) => storage.isValidUuid(c.id));
  applyAndPrimeCheats();
  renderCheatList();
}

async function setCheatEnabled(id, enabled) {
  await storage.updateCheat(id, { enabled });
  const cheat = state.cheats.find((c) => c.id === id);
  if (cheat) cheat.enabled = enabled;
  applyAndPrimeCheats();
  showToast(enabled ? 'Cheat enabled' : 'Cheat disabled');
  pushCheatsIfSignedIn();
}

async function removeCheat(id) {
  await storage.deleteCheat(id);
  state.cheats = state.cheats.filter((c) => c.id !== id);
  applyAndPrimeCheats();
  renderCheatList();
  pushCheatsIfSignedIn();
}

function pushCheatsIfSignedIn() {
  if (state.syncSession) sync.pushDirtyCheats().catch((err) => setSyncStatus(`Sync failed: ${err?.message ?? err}`, true));
}

function renderCheatList() {
  el.cheatList.replaceChildren();
  el.btnCheatAdd.disabled = !state.currentGame;
  renderCheatPresets();

  if (!state.currentGame) {
    el.cheatList.append(makeEmptyItem('Load a ROM to manage cheats.'));
    return;
  }

  if (state.cheats.length === 0) {
    el.cheatList.append(makeEmptyItem('No cheats added for this game yet.'));
    return;
  }

  for (const cheat of state.cheats) {
    const li = document.createElement('li');
    li.classList.toggle('cheat-row-disabled', !cheat.enabled);

    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.checked = cheat.enabled;
    toggle.title = cheat.enabled ? 'Enabled -- click to disable' : 'Disabled -- click to enable';
    toggle.setAttribute('data-testid', `cheat-toggle-${cheat.id}`);
    toggle.addEventListener('change', () => setCheatEnabled(cheat.id, toggle.checked));

    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = cheat.description || cheat.code;

    const code = document.createElement('span');
    code.className = 'meta cheat-row-code';
    // Only show the raw code separately once a description makes the name
    // slot say something else -- otherwise it would just repeat itself.
    code.textContent = cheat.description ? cheat.code : '';

    const remove = document.createElement('button');
    remove.className = 'danger';
    remove.textContent = 'Remove';
    remove.addEventListener('click', () => removeCheat(cheat.id));

    li.append(toggle, name, code, remove);
    el.cheatList.append(li);
  }
}

/* ------------------------------------------------------- game editors -- */

function wireGameEditor() {
  el.btnGameEditorRefresh.addEventListener('click', () => {
    renderGameEditor();
    log('Party editor refreshed from the running game.');
  });
}

/**
 * Show/hide and (re)populate the party-editor panel for whatever
 * game-editors/ plugin matched the currently loaded ROM, if any -- see
 * state.gameEditor (set in startRom()) and game-editors/index.js.
 */
function renderGameEditor() {
  const plugin = state.gameEditor;
  el.gameEditorPanel.hidden = !plugin;
  if (!plugin) return;

  el.gameEditorGameName.textContent = `(${plugin.name})`;

  el.gameEditorHead.replaceChildren();
  const nameHead = document.createElement('th');
  nameHead.textContent = 'Character';
  el.gameEditorHead.append(nameHead);
  for (const field of plugin.fields) {
    const th = document.createElement('th');
    th.textContent = field.label;
    el.gameEditorHead.append(th);
  }
  if (plugin.revive) {
    const th = document.createElement('th');
    th.textContent = 'Status';
    el.gameEditorHead.append(th);
  }

  const columnCount = plugin.fields.length + 1 + (plugin.revive ? 1 : 0);

  el.gameEditorBody.replaceChildren();
  const characters = plugin.read(state.core);

  el.gameEditorEquipmentTitle.hidden = !plugin.equipment;
  el.gameEditorEsperTitle.hidden = !plugin.espers;
  el.gameEditorAilmentsTitle.hidden = !plugin.ailments;
  el.gameEditorAilmentsList.hidden = !plugin.ailments;
  el.gameEditorSpellsTitle.hidden = !plugin.spells;
  el.gameEditorSpellsList.hidden = !plugin.spells;
  el.gameEditorAbilitiesTitle.hidden = !plugin.lore;
  el.gameEditorAbilitiesList.hidden = !plugin.lore;
  el.gameEditorEsperInventoryTitle.hidden = !plugin.esperInventory;
  el.gameEditorEsperInventoryList.hidden = !plugin.esperInventory;
  el.gameEditorBlitzesTitle.hidden = !plugin.blitzes;
  el.gameEditorBlitzesList.hidden = !plugin.blitzes;
  el.gameEditorSwdtechsTitle.hidden = !plugin.swdtechs;
  el.gameEditorSwdtechsList.hidden = !plugin.swdtechs;
  el.gameEditorRagesTitle.hidden = !plugin.rages;
  el.gameEditorRagesList.hidden = !plugin.rages;
  el.gameEditorDancesTitle.hidden = !plugin.dances;
  el.gameEditorDancesList.hidden = !plugin.dances;
  el.gameEditorGpTitle.hidden = !plugin.readGp;
  el.gameEditorGpRow.hidden = !plugin.readGp;
  el.gameEditorInventoryTitle.hidden = !plugin.inventoryItems;
  el.gameEditorInventoryList.hidden = !plugin.inventoryItems;
  el.gameEditorKeyItemsTitle.hidden = !plugin.keyItems;
  el.gameEditorKeyItemsList.hidden = !plugin.keyItems;
  el.gameEditorItemBonusTitle.hidden = !plugin.itemBonusItems;
  el.gameEditorItemBonusHint.hidden = !plugin.itemBonusItems;
  el.gameEditorItemBonusBody.hidden = !plugin.itemBonusItems;

  if (characters.length === 0) {
    const row = document.createElement('tr');
    const cell = document.createElement('td');
    cell.colSpan = columnCount;
    cell.className = 'dim';
    cell.textContent = 'No recognized party members in memory right now -- get further into the game and refresh.';
    row.append(cell);
    el.gameEditorBody.append(row);
    el.gameEditorEquipmentHead.replaceChildren();
    el.gameEditorEquipmentBody.replaceChildren();
    el.gameEditorEsperHead.replaceChildren();
    el.gameEditorEsperBody.replaceChildren();
    el.gameEditorAilmentsList.replaceChildren();
    el.gameEditorSpellsList.replaceChildren();
    if (plugin.blitzes) renderGameEditorBlitzes(plugin);
    if (plugin.swdtechs) renderGameEditorSwdtechs(plugin);
    if (plugin.rages) renderGameEditorRages(plugin);
    if (plugin.dances) renderGameEditorDances(plugin);
    if (plugin.readGp) renderGameEditorGp(plugin);
    if (plugin.lore) renderGameEditorAbilities(plugin);
    if (plugin.esperInventory) renderGameEditorEsperInventory(plugin);
    if (plugin.inventoryItems) renderGameEditorInventory(plugin);
    if (plugin.keyItems) renderGameEditorKeyItems(plugin);
    if (plugin.itemBonusItems) renderGameEditorItemBonus(plugin);
    return;
  }

  for (const character of characters) {
    const row = document.createElement('tr');
    const nameCell = document.createElement('td');
    nameCell.textContent = character.name;
    row.append(nameCell);

    for (const field of plugin.fields) {
      const cell = document.createElement('td');
      const input = document.createElement('input');
      input.setAttribute('data-testid', `game-editor-${character.slot}-${field.key}`);

      if (field.type === 'text') {
        input.type = 'text';
        input.maxLength = field.maxLength;
        input.value = character[field.key];
        input.addEventListener('change', () => {
          plugin.write(state.core, character.slot, field.key, input.value);
          input.value = plugin.read(state.core).find((c) => c.slot === character.slot)?.[field.key] ?? input.value;
          input.classList.remove('applied');
          void input.offsetWidth;
          input.classList.add('applied');
          setTimeout(() => input.classList.remove('applied'), 1200);
          log(`Set ${character.name}'s ${field.label} to "${input.value}".`);
        });
      } else {
        input.type = 'number';
        input.min = String(field.min);
        input.max = String(field.max);
        input.value = String(character[field.key]);
        input.addEventListener('change', () => {
          const clamped = Math.min(field.max, Math.max(field.min, Math.round(Number(input.value) || 0)));
          input.value = String(clamped);
          plugin.write(state.core, character.slot, field.key, clamped);
          input.classList.remove('applied');
          void input.offsetWidth; // restart the CSS transition on repeated edits
          input.classList.add('applied');
          setTimeout(() => input.classList.remove('applied'), 1200);
          log(`Set ${character.name}'s ${field.label} to ${clamped}.`);
        });
      }
      cell.append(input);

      if (field.fillFrom) {
        const fill = document.createElement('button');
        fill.type = 'button';
        fill.className = 'button game-editor-fill-button';
        fill.textContent = 'Full';
        fill.title = `Set to ${plugin.fields.find((f) => f.key === field.fillFrom)?.label ?? field.fillFrom}`;
        fill.setAttribute('data-testid', `game-editor-${character.slot}-${field.key}-fill`);
        fill.addEventListener('click', () => {
          input.value = String(character[field.fillFrom]);
          input.dispatchEvent(new Event('change'));
        });
        cell.append(fill);
      }
      row.append(cell);
    }

    if (plugin.revive) {
      const cell = document.createElement('td');
      if (character.dead) {
        const badge = document.createElement('span');
        badge.className = 'game-editor-status-dead';
        badge.textContent = 'KO';
        const revive = document.createElement('button');
        revive.type = 'button';
        revive.className = 'button';
        revive.textContent = 'Revive';
        revive.setAttribute('data-testid', `game-editor-${character.slot}-revive`);
        revive.addEventListener('click', () => {
          plugin.revive(state.core, character.slot);
          log(`Revived ${character.name}.`);
          renderGameEditor();
        });
        cell.append(badge, revive);
      } else {
        cell.textContent = 'OK';
        cell.className = 'dim';
      }
      row.append(cell);
    }
    el.gameEditorBody.append(row);
  }

  if (plugin.equipment) renderGameEditorEquipment(plugin, characters);
  if (plugin.espers) renderGameEditorEspers(plugin, characters);
  if (plugin.ailments) renderGameEditorAilments(plugin, characters);
  if (plugin.spells) renderGameEditorSpells(plugin, characters);
  if (plugin.lore) renderGameEditorAbilities(plugin);
  if (plugin.esperInventory) renderGameEditorEsperInventory(plugin);
  if (plugin.blitzes) renderGameEditorBlitzes(plugin);
  if (plugin.swdtechs) renderGameEditorSwdtechs(plugin);
  if (plugin.rages) renderGameEditorRages(plugin);
  if (plugin.dances) renderGameEditorDances(plugin);
  if (plugin.readGp) renderGameEditorGp(plugin);
  if (plugin.inventoryItems) renderGameEditorInventory(plugin);
  if (plugin.keyItems) renderGameEditorKeyItems(plugin);
  if (plugin.itemBonusItems) renderGameEditorItemBonus(plugin);
}

/** Equipment table: one row per character, one <select> column per equipment slot. */
function renderGameEditorEquipment(plugin, characters) {
  renderSelectTable(plugin, el.gameEditorEquipmentHead, el.gameEditorEquipmentBody, plugin.equipment, characters);
}

function renderGameEditorEspers(plugin, characters) {
  renderSelectTable(plugin, el.gameEditorEsperHead, el.gameEditorEsperBody, plugin.espers, characters);
}

/**
 * A plain <select> paired with a text input that filters its visible
 * <option>s by case-insensitive substring match -- for the Item Editor's
 * and Inventory's item pickers, both drawn from catalogues in the low
 * hundreds where scrolling a plain dropdown to find one entry is slow.
 * Filtering hides non-matching options (a native <select> fully supports
 * per-option `hidden`) rather than rebuilding the list, so the already-
 * selected value and every existing `change` handler on the select are
 * completely unaffected by whatever's currently typed into the search box.
 *
 * @param {object} opts
 * @param {string} opts.testId base data-testid; the search input becomes `${testId}-search`
 * @param {Array<{value?: string|number, id?: string|number, label: string}>} opts.options
 *   either `{value,label}` (equipment-style) or `{id,label}` (catalogue-style) entries
 * @param {string} [opts.placeholderLabel] an always-visible, non-filterable
 *   first option (value `''`) -- e.g. "— choose an item —"
 * @param {string} [opts.searchValue] initial search text (for persisting it across re-renders)
 * @param {(text: string) => void} [opts.onSearch] fires on every keystroke
 * @returns {{wrap: HTMLElement, select: HTMLSelectElement, search: HTMLInputElement}}
 */
function createSearchableSelect({ testId, options, placeholderLabel, searchValue = '', onSearch }) {
  const wrap = document.createElement('div');
  wrap.className = 'game-editor-searchable-select';

  const search = document.createElement('input');
  search.type = 'search';
  search.placeholder = 'Search…';
  search.value = searchValue;
  search.className = 'game-editor-search-input';
  search.setAttribute('data-testid', `${testId}-search`);

  const select = document.createElement('select');
  select.setAttribute('data-testid', testId);

  if (placeholderLabel !== undefined) {
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = placeholderLabel;
    select.append(placeholder);
  }

  const optionEntries = options.map((opt) => {
    const optionEl = document.createElement('option');
    optionEl.value = String(opt.value ?? opt.id);
    optionEl.textContent = opt.label;
    select.append(optionEl);
    return { optionEl, needleLabel: opt.label.toLowerCase() };
  });

  function applyFilter(text) {
    const needle = text.trim().toLowerCase();
    for (const { optionEl, needleLabel } of optionEntries) {
      optionEl.hidden = needle.length > 0 && !needleLabel.includes(needle);
    }
  }
  applyFilter(searchValue);

  search.addEventListener('input', () => {
    onSearch?.(search.value);
    applyFilter(search.value);
  });

  wrap.append(search, select);
  return { wrap, select, search };
}

/**
 * One row per character, one <select> column per field in `fieldList` --
 * shared by the Equipment and Esper Bond tables, which are otherwise
 * identical (a table of dropdowns keyed by a plugin field's `options`) and
 * differ only in which fields and which <thead>/<tbody> elements they use.
 */
function renderSelectTable(plugin, headEl, bodyEl, fieldList, characters) {
  headEl.replaceChildren();
  const nameHead = document.createElement('th');
  nameHead.textContent = 'Character';
  headEl.append(nameHead);
  for (const field of fieldList) {
    const th = document.createElement('th');
    th.textContent = field.label;
    headEl.append(th);
  }

  bodyEl.replaceChildren();
  for (const character of characters) {
    const row = document.createElement('tr');
    const nameCell = document.createElement('td');
    nameCell.textContent = character.name;
    row.append(nameCell);

    for (const field of fieldList) {
      const cell = document.createElement('td');
      const select = document.createElement('select');
      select.setAttribute('data-testid', `game-editor-${character.slot}-${field.key}`);
      // A plugin can widen a slot's choices per character -- FF6's off-hand
      // accepts a weapon once the Genji Glove effect is equipped.
      const options = plugin.equipmentOptionsFor?.(state.core, character, field.key) ?? field.options;
      for (const option of options) {
        const opt = document.createElement('option');
        opt.value = String(option.value);
        opt.textContent = option.label;
        select.append(opt);
      }
      select.value = String(character[field.key]);
      select.addEventListener('change', () => {
        const value = Number(select.value);
        plugin.write(state.core, character.slot, field.key, value);
        const label = field.options.find((o) => o.value === value)?.label ?? value;
        select.classList.remove('applied');
        void select.offsetWidth;
        select.classList.add('applied');
        setTimeout(() => select.classList.remove('applied'), 1200);
        log(`Set ${character.name}'s ${field.label} to ${label}.`);
      });
      cell.append(select);
      row.append(cell);
    }
    bodyEl.append(row);
  }
}

/** Ailments list: one row per character, a "cure" chip per currently-active ailment. */
function renderGameEditorAilments(plugin, characters) {
  el.gameEditorAilmentsList.replaceChildren();
  for (const character of characters) {
    const li = document.createElement('li');
    li.className = 'chip-row';
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = character.name;
    li.append(name);

    if (character.activeAilments.length === 0) {
      const none = document.createElement('span');
      none.className = 'meta';
      none.textContent = 'No status ailments';
      li.append(none);
    } else {
      for (const key of character.activeAilments) {
        const ailment = plugin.ailments.find((a) => a.key === key);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'chip';
        chip.textContent = `${ailment?.label ?? key} ×`;
        chip.title = `Cure ${ailment?.label ?? key}`;
        chip.setAttribute('data-testid', `game-editor-${character.slot}-ailment-${key}`);
        chip.addEventListener('click', () => {
          plugin.clearAilment(state.core, character.slot, key);
          log(`Cured ${character.name}'s ${ailment?.label ?? key}.`);
          renderGameEditor();
        });
        li.append(chip);
      }
    }
    el.gameEditorAilmentsList.append(li);
  }
}

/**
 * Spells list: one row per character, a "forget" chip per known spell plus
 * a dropdown + button to learn any spell they don't already know. Spells
 * are addressed by character ID (permanent), not by party slot, so
 * plugin.learnSpell()/forgetSpell() take `character.charId` -- unlike every
 * other game-editor action above, which takes the party slot.
 */
function renderGameEditorSpells(plugin, characters) {
  el.gameEditorSpellsList.replaceChildren();
  for (const character of characters) {
    const li = document.createElement('li');
    li.className = 'chip-row';
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = character.name;
    li.append(name);

    for (const spellId of character.knownSpellIds) {
      const spell = plugin.spells.find((s) => s.id === spellId);
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'chip';
      chip.textContent = `${spell?.label ?? spellId} ×`;
      chip.title = `Forget ${spell?.label ?? spellId}`;
      chip.setAttribute('data-testid', `game-editor-spell-${character.slot}-${spellId}`);
      chip.addEventListener('click', () => {
        plugin.forgetSpell(state.core, character.charId, spellId);
        log(`${character.name} forgot ${spell?.label ?? spellId}.`);
        renderGameEditor();
      });
      li.append(chip);
    }

    const known = new Set(character.knownSpellIds);
    const unknown = plugin.spells.filter((s) => !known.has(s.id));
    if (unknown.length > 0) {
      const select = document.createElement('select');
      select.setAttribute('data-testid', `game-editor-spell-select-${character.slot}`);
      for (const spell of unknown) {
        const opt = document.createElement('option');
        opt.value = String(spell.id);
        opt.textContent = spell.label;
        select.append(opt);
      }
      const learn = document.createElement('button');
      learn.type = 'button';
      learn.className = 'button';
      learn.textContent = 'Learn';
      learn.setAttribute('data-testid', `game-editor-spell-learn-${character.slot}`);
      learn.addEventListener('click', () => {
        const spellId = Number(select.value);
        const spell = plugin.spells.find((s) => s.id === spellId);
        plugin.learnSpell(state.core, character.charId, spellId);
        log(`${character.name} learned ${spell?.label ?? spellId}.`);
        renderGameEditor();
      });
      const learnAll = document.createElement('button');
      learnAll.type = 'button';
      learnAll.className = 'button';
      learnAll.textContent = `Learn all (${unknown.length})`;
      learnAll.title = `Teach ${character.name} every spell they don't already know`;
      learnAll.setAttribute('data-testid', `game-editor-spell-learn-all-${character.slot}`);
      learnAll.addEventListener('click', () => {
        for (const spell of unknown) plugin.learnSpell(state.core, character.charId, spell.id);
        log(`${character.name} learned all ${unknown.length} remaining spells.`);
        renderGameEditor();
      });
      li.append(select, learn, learnAll);
    }
    el.gameEditorSpellsList.append(li);
  }
}

/**
 * Abilities list: currently just Strago's Lore, which is global save data
 * (not tied to any character or party slot) -- rendered as a single row,
 * same chip-plus-learn-form pattern as renderGameEditorSpells().
 */
function renderGameEditorAbilities(plugin) {
  const { knownIds } = plugin.readLore(state.core);
  renderGlobalChipRow(el.gameEditorAbilitiesList, {
    rowLabel: "Strago's Lore",
    testId: 'lore',
    allItems: plugin.lore,
    knownIds,
    onLearn: (id) => plugin.learnLore(state.core, id),
    onForget: (id) => plugin.forgetLore(state.core, id),
    noun: 'Lore',
    learnAll: true,
  });
}

function renderGameEditorEsperInventory(plugin) {
  const { knownIds } = plugin.readEsperInventory(state.core);
  renderGlobalChipRow(el.gameEditorEsperInventoryList, {
    rowLabel: 'Espers owned',
    testId: 'esper-owned',
    allItems: plugin.esperInventory,
    knownIds,
    onLearn: (id) => plugin.ownEsper(state.core, id),
    onForget: (id) => plugin.disownEsper(state.core, id),
    noun: 'Esper',
  });
}

function renderGameEditorKeyItems(plugin) {
  const { knownIds } = plugin.readKeyItems(state.core);
  renderGlobalChipRow(el.gameEditorKeyItemsList, {
    rowLabel: 'Key items',
    testId: 'key-item',
    allItems: plugin.keyItems,
    knownIds,
    onLearn: (id) => plugin.obtainKeyItem(state.core, id),
    onForget: (id) => plugin.removeKeyItem(state.core, id),
    noun: 'key item',
    addVerb: 'Obtained',
    removeVerb: 'Removed',
  });
}

/**
 * Item editor: pick one item, then edit its Power (ATK/DEF)/Strength/Speed/
 * Stamina/Magic Power bonuses. Unlike everything else in the party editor,
 * committing an edit here doesn't write WRAM -- it installs (or removes) a
 * real Game Genie/PAR cheat through the exact same storage the Cheats panel
 * uses (`storage.putCheat`/`deleteCheat`, `refreshCheatList()`), since the
 * underlying data lives in the ROM and only a re-applied-every-frame cheat
 * survives a reload; see ff6.js's block comment above ITEM_PROP_BUS_BASE.
 * `state.gameEditorItemBonusId` persists the dropdown's selection across the
 * full-panel re-renders every other edit already triggers, so picking an
 * item and then tweaking several of its fields doesn't reset the picker
 * each time.
 */
async function renderGameEditorItemBonus(plugin) {
  el.gameEditorItemBonusBody.replaceChildren();

  const { wrap: pickerWrap, select: picker } = createSearchableSelect({
    testId: 'game-editor-item-bonus-picker',
    options: plugin.itemBonusItems,
    placeholderLabel: '— choose an item —',
    searchValue: state.gameEditorItemBonusSearch,
    onSearch: (text) => {
      state.gameEditorItemBonusSearch = text;
    },
  });
  picker.value = state.gameEditorItemBonusId !== null ? String(state.gameEditorItemBonusId) : '';
  picker.addEventListener('change', () => {
    state.gameEditorItemBonusId = picker.value === '' ? null : Number(picker.value);
    renderGameEditorItemBonus(plugin);
  });
  el.gameEditorItemBonusBody.append(pickerWrap);

  if (state.gameEditorItemBonusId === null) return;

  const itemId = state.gameEditorItemBonusId;
  const itemName = plugin.itemBonusItems.find((i) => i.id === itemId)?.label ?? String(itemId);
  const current = plugin.readItemBonus(state.core, itemId);

  const row = document.createElement('div');
  row.className = 'game-editor-item-bonus-fields';

  const applyCheatResult = async (result, fieldLabel) => {
    if (!state.currentGame) return;
    const gameId = state.currentGame.id;
    // result.id is a plain, human-readable SEED ("ff6-item-power-0"), not a
    // storage id -- the cheats table's id column is a Postgres uuid, so it
    // gets hashed into one here, the one place that needs to know that.
    const id = await storage.deterministicUuid(result.id);
    if (result.code) {
      await storage.putCheat(id, gameId, result.code, `Item Editor: ${itemName} ${fieldLabel}`, true);
    } else {
      await storage.deleteCheat(id);
    }
    await refreshCheatList();
    pushCheatsIfSignedIn();
  };

  const readPaired = (otherKey, ownKey, ownValue) => {
    if (ownKey === otherKey) return ownValue;
    const otherInput = el.gameEditorItemBonusBody.querySelector(`[data-testid="game-editor-item-bonus-${otherKey}"]`);
    return Number(otherInput.value);
  };

  for (const field of plugin.itemBonusFields) {
    if (field.excludeItemIds?.has(itemId)) continue; // this byte doesn't mean this field for this particular item -- see ff6.js
    const wrap = document.createElement('label');
    wrap.className = 'game-editor-item-bonus-field';
    const labelText = document.createElement('span');
    labelText.textContent = field.label;
    const input = document.createElement('input');
    input.type = 'number';
    input.min = String(field.min);
    input.max = String(field.max);
    if (field.step) input.step = String(field.step);
    input.value = String(current[field.key]);
    input.setAttribute('data-testid', `game-editor-item-bonus-${field.key}`);
    input.addEventListener('change', async () => {
      const clamped = Math.min(field.max, Math.max(field.min, Math.round(Number(input.value) || 0)));
      input.value = String(clamped);

      let result;
      if (field.key === 'power') {
        result = plugin.buildPowerCheat(state.core, itemId, clamped);
      } else if (field.key === 'hitRateOrMagDef') {
        result = plugin.buildHitRateMagDefCheat(state.core, itemId, clamped);
      } else if (field.key === 'strength' || field.key === 'speed') {
        const strength = readPaired('strength', field.key, clamped);
        const speed = readPaired('speed', field.key, clamped);
        result = plugin.buildStrengthSpeedCheat(state.core, itemId, strength, speed);
      } else if (field.key === 'stamina' || field.key === 'magicPower') {
        const stamina = readPaired('stamina', field.key, clamped);
        const magicPower = readPaired('magicPower', field.key, clamped);
        result = plugin.buildStaminaMagicCheat(state.core, itemId, stamina, magicPower);
      } else {
        const evade = readPaired('evade', field.key, clamped);
        const mblock = readPaired('mblock', field.key, clamped);
        result = plugin.buildEvadeMblockCheat(state.core, itemId, evade, mblock);
      }

      await applyCheatResult(result, field.label);
      log(`Set ${itemName}'s ${field.label} to ${clamped}.`);
      // Full re-render rather than just flashing this input: a cheat may
      // have just appeared or disappeared, and the "share as a raw code"
      // block below needs to reflect that immediately, not just on the
      // next unrelated refresh.
      renderGameEditorItemBonus(plugin);
      const fresh = el.gameEditorItemBonusBody.querySelector(`[data-testid="game-editor-item-bonus-${field.key}"]`);
      if (fresh) {
        fresh.classList.add('applied');
        setTimeout(() => fresh.classList.remove('applied'), 1200);
      }
    });
    wrap.append(labelText, input);
    row.append(wrap);
  }
  el.gameEditorItemBonusBody.append(row);

  // Spell cast on hit: a select (id -1 = "no spell") plus the two flag
  // checkboxes, all combined into a single byte/cheat -- not a plain
  // number field, so it's handled separately from the itemBonusFields loop
  // above rather than trying to force it into that generic shape.
  const spellCastWrap = document.createElement('div');
  spellCastWrap.className = 'game-editor-item-bonus-field game-editor-item-bonus-spellcast';
  const spellCastLabel = document.createElement('span');
  spellCastLabel.textContent = 'Spell Cast on Hit';
  spellCastWrap.append(spellCastLabel);

  const { wrap: spellCastPickerWrap, select: spellCastSelect } = createSearchableSelect({
    testId: 'game-editor-item-bonus-spellcast',
    options: [{ id: -1, label: 'None (no spell)' }, ...plugin.spells],
    searchValue: '',
  });
  spellCastSelect.value = String(current.spellCastId);
  spellCastWrap.append(spellCastPickerWrap);

  const randomCheckLabel = document.createElement('label');
  randomCheckLabel.className = 'game-editor-item-bonus-checkbox';
  const randomCheck = document.createElement('input');
  randomCheck.type = 'checkbox';
  randomCheck.checked = current.spellCastRandom;
  randomCheck.setAttribute('data-testid', 'game-editor-item-bonus-spellcast-random');
  randomCheckLabel.append(randomCheck, document.createTextNode(' Random chance (not every hit)'));
  spellCastWrap.append(randomCheckLabel);

  const useAsItemCheckLabel = document.createElement('label');
  useAsItemCheckLabel.className = 'game-editor-item-bonus-checkbox';
  const useAsItemCheck = document.createElement('input');
  useAsItemCheck.type = 'checkbox';
  useAsItemCheck.checked = current.spellCastUseAsItem;
  useAsItemCheck.setAttribute('data-testid', 'game-editor-item-bonus-spellcast-useitem');
  useAsItemCheckLabel.append(useAsItemCheck, document.createTextNode(' Also usable as an item, to cast the spell'));
  spellCastWrap.append(useAsItemCheckLabel);

  const applySpellCast = async () => {
    const spellId = Number(spellCastSelect.value);
    const result = plugin.buildSpellCastCheat(state.core, itemId, spellId, randomCheck.checked, useAsItemCheck.checked);
    await applyCheatResult(result, 'Spell Cast on Hit');
    const spellName = spellId < 0 ? 'no spell' : (plugin.spells.find((s) => s.id === spellId)?.label ?? String(spellId));
    log(`Set ${itemName}'s Spell Cast on Hit to ${spellName}.`);
    renderGameEditorItemBonus(plugin);
    const fresh = el.gameEditorItemBonusBody.querySelector('.game-editor-item-bonus-spellcast');
    if (fresh) {
      fresh.classList.add('applied');
      setTimeout(() => fresh.classList.remove('applied'), 1200);
    }
  };
  spellCastSelect.addEventListener('change', applySpellCast);
  randomCheck.addEventListener('change', applySpellCast);
  useAsItemCheck.addEventListener('change', applySpellCast);

  el.gameEditorItemBonusBody.append(spellCastWrap);

  // Weapon Special Effect (offset 27's high nibble): a single select, not a
  // plain number -- same reason Spell Cast on Hit is its own block above
  // rather than living in the itemBonusFields loop. Hidden entirely for the
  // 5 CONSUMABLE-typed "Edge" items, same exclusion as Hit Rate/Mag. Def.
  if (!plugin.weaponSpecialExcludedItemIds?.has(itemId)) {
    const weaponSpecialWrap = document.createElement('div');
    weaponSpecialWrap.className = 'game-editor-item-bonus-field game-editor-item-bonus-spellcast game-editor-item-bonus-weaponspecial';
    const weaponSpecialLabel = document.createElement('span');
    weaponSpecialLabel.textContent = 'Weapon Special Effect';
    weaponSpecialWrap.append(weaponSpecialLabel);

    const { wrap: weaponSpecialPickerWrap, select: weaponSpecialSelect } = createSearchableSelect({
      testId: 'game-editor-item-bonus-weaponspecial',
      options: plugin.weaponSpecialOptions,
      searchValue: '',
    });
    weaponSpecialSelect.value = String(current.weaponSpecial);
    weaponSpecialWrap.append(weaponSpecialPickerWrap);

    weaponSpecialSelect.addEventListener('change', async () => {
      const value = Number(weaponSpecialSelect.value);
      const result = plugin.buildWeaponSpecialCheat(state.core, itemId, value);
      await applyCheatResult(result, 'Weapon Special Effect');
      const effectName = plugin.weaponSpecialOptions.find((o) => o.id === value)?.label ?? String(value);
      log(`Set ${itemName}'s Weapon Special Effect to ${effectName}.`);
      renderGameEditorItemBonus(plugin);
      const fresh = el.gameEditorItemBonusBody.querySelector('.game-editor-item-bonus-weaponspecial');
      if (fresh) {
        fresh.classList.add('applied');
        setTimeout(() => fresh.classList.remove('applied'), 1200);
      }
    });

    el.gameEditorItemBonusBody.append(weaponSpecialWrap);
  }

  // Flag groups (relic effects, field effects, granted status, immunity,
  // elements): all the same shape -- a checklist over one or more whole
  // bytes, where each byte is a single cheat recomputed from whichever
  // checkboxes share it. Driven entirely by plugin.itemFlagGroups, so
  // adding a group needs no change here.
  //
  // Collapsed by default: there are over 100 flags across the groups, and
  // leaving them all expanded buries the rest of the editor (and is an
  // enormous scroll on a phone).
  for (const group of plugin.itemFlagGroups ?? []) {
    if (group.excludeItemIds?.has(itemId)) continue;

    const details = document.createElement('details');
    details.className = 'game-editor-item-bonus-field game-editor-item-bonus-spellcast game-editor-flag-group';
    details.setAttribute('data-testid', `game-editor-item-bonus-group-${group.id}`);

    const active = group.flags.filter((f) => (current.flagBytes[f.offset] & f.bit) !== 0).length;
    const summary = document.createElement('summary');
    summary.textContent = active > 0 ? `${group.label} (${active} on)` : group.label;
    details.append(summary);

    const grid = document.createElement('div');
    grid.className = 'game-editor-relic-grid';

    const applyByte = async (offsetKey) => {
      let value = 0;
      for (const flag of group.flags) {
        if (flag.offset !== offsetKey) continue;
        const box = grid.querySelector(`[data-testid="game-editor-item-bonus-flag-${flag.key}"]`);
        if (box?.checked) value |= flag.bit;
      }
      const result = plugin.buildFlagByteCheat(state.core, itemId, offsetKey, value);
      await applyCheatResult(result, group.label);
    };

    for (const flag of group.flags) {
      const row = document.createElement('label');
      row.className = 'game-editor-item-bonus-checkbox';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = (current.flagBytes[flag.offset] & flag.bit) !== 0;
      box.setAttribute('data-testid', `game-editor-item-bonus-flag-${flag.key}`);
      box.addEventListener('change', async () => {
        await applyByte(flag.offset);
        log(`${box.checked ? 'Enabled' : 'Disabled'} \u201c${flag.label}\u201d on ${itemName}.`);
        // Keep this group open across the re-render -- collapsing it on
        // every tick would make setting several flags miserable.
        state.gameEditorOpenFlagGroups.add(group.id);
        renderGameEditorItemBonus(plugin);
        const fresh = el.gameEditorItemBonusBody.querySelector(`[data-testid="game-editor-item-bonus-group-${group.id}"]`);
        if (fresh) {
          fresh.classList.add('applied');
          setTimeout(() => fresh.classList.remove('applied'), 1200);
        }
      });
      row.append(box, document.createTextNode(` ${flag.label}`));
      grid.append(row);
    }

    details.open = state.gameEditorOpenFlagGroups.has(group.id);
    details.addEventListener('toggle', () => {
      if (details.open) state.gameEditorOpenFlagGroups.add(group.id);
      else state.gameEditorOpenFlagGroups.delete(group.id);
    });

    details.append(grid);
    el.gameEditorItemBonusBody.append(details);
  }

  // Whichever of this item's auto-generated cheats currently exist, shown
  // as a plain readonly/selectable code + Copy button, so a
  // change made through the friendly fields above can still be shared with
  // someone else as a raw Game Genie/PAR-style code, without them needing
  // this editor at all. Same seed -> storage-id hash as applyCheatResult().
  const cheatSeeds = plugin.itemBonusCheatIds(itemId);
  const cheatIdList = await Promise.all(Object.values(cheatSeeds).map((seed) => storage.deterministicUuid(seed)));
  const activeCheats = cheatIdList.map((id) => state.cheats.find((c) => c.id === id)).filter(Boolean);
  if (activeCheats.length > 0) {
    const codesWrap = document.createElement('div');
    codesWrap.className = 'game-editor-item-bonus-codes';
    const heading = document.createElement('p');
    heading.className = 'dim';
    heading.textContent = 'Share as a raw code:';
    codesWrap.append(heading);
    for (const cheat of activeCheats) {
      const line = document.createElement('div');
      line.className = 'game-editor-item-bonus-code-row';

      const codeInput = document.createElement('input');
      codeInput.type = 'text';
      codeInput.readOnly = true;
      codeInput.value = cheat.code;
      codeInput.className = 'game-editor-item-bonus-code';
      codeInput.title = cheat.description;
      codeInput.setAttribute('data-testid', `game-editor-item-bonus-code-${cheat.id}`);
      codeInput.addEventListener('click', () => codeInput.select());

      const copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'button';
      copy.textContent = 'Copy';
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(cheat.code);
          log(`Copied code ${cheat.code} to the clipboard.`);
        } catch {
          codeInput.select();
          log('Could not access the clipboard -- the code is selected, copy it with Ctrl/Cmd+C.', true);
        }
      });

      line.append(codeInput, copy);
      codesWrap.append(line);
    }
    el.gameEditorItemBonusBody.append(codesWrap);
  }
}

/**
 * One `<li>` row (into `listEl`) for a global, not-per-character, "own this
 * or not" list: a chip per currently-known/owned entry (click to forget)
 * plus a dropdown + button to add any entry not already known -- shared by
 * Abilities (Lore), Esper Inventory, and Key Items, which are otherwise
 * identical in shape (a `{id,label}[]` catalogue plus a `knownIds` subset).
 * `addVerb`/`removeVerb` default to "Learned"/"Forgot" (Lore/spells) but
 * can be overridden for a better fit elsewhere (e.g. "Obtained"/"Removed"
 * for key items).
 */
function renderGlobalChipRow(
  listEl,
  { rowLabel, testId, allItems, knownIds, onLearn, onForget, noun, addVerb = 'Learned', removeVerb = 'Forgot', learnAll = false },
) {
  listEl.replaceChildren();
  const li = document.createElement('li');
  li.className = 'chip-row';
  const name = document.createElement('span');
  name.className = 'name';
  name.textContent = rowLabel;
  li.append(name);

  for (const id of knownIds) {
    const item = allItems.find((entry) => entry.id === id);
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'chip';
    chip.textContent = `${item?.label ?? id} ×`;
    chip.title = `${removeVerb} ${item?.label ?? id}`;
    chip.setAttribute('data-testid', `game-editor-${testId}-${id}`);
    chip.addEventListener('click', () => {
      onForget(id);
      log(`${removeVerb} the ${noun} "${item?.label ?? id}".`);
      renderGameEditor();
    });
    li.append(chip);
  }

  const known = new Set(knownIds);
  const unknown = allItems.filter((entry) => !known.has(entry.id));
  if (unknown.length > 0) {
    const select = document.createElement('select');
    select.setAttribute('data-testid', `game-editor-${testId}-select`);
    for (const item of unknown) {
      const opt = document.createElement('option');
      opt.value = String(item.id);
      opt.textContent = item.label;
      select.append(opt);
    }
    const learn = document.createElement('button');
    learn.type = 'button';
    learn.className = 'button';
    learn.textContent = addVerb === 'Obtained' ? 'Add' : 'Learn';
    learn.setAttribute('data-testid', `game-editor-${testId}-learn`);
    learn.addEventListener('click', () => {
      const id = Number(select.value);
      const item = allItems.find((entry) => entry.id === id);
      onLearn(id);
      log(`${addVerb} the ${noun} "${item?.label ?? id}".`);
      renderGameEditor();
    });
    li.append(select, learn);

    // Opt-in: only the row that asked for it gets a bulk action, so the
    // Esper and key-item rows stay exactly as they were.
    if (learnAll) {
      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'button';
      all.textContent = `Learn all (${unknown.length})`;
      all.setAttribute('data-testid', `game-editor-${testId}-learn-all`);
      all.addEventListener('click', () => {
        for (const item of unknown) onLearn(item.id);
        log(`${addVerb} all ${unknown.length} remaining ${noun}s.`);
        renderGameEditor();
      });
      li.append(all);
    }
  }
  listEl.append(li);
}

/**
 * General inventory: global save data, one row per occupied slot (item
 * name, an editable quantity, a Remove button) plus a trailing add-item
 * form (item select + quantity + Add). Unlike the chip lists above, an
 * inventory slot carries a quantity, not just presence/absence, so each
 * row needs its own number input rather than a single removable chip.
 */
/**
 * Sabin's Blitzes, Cyan's SwdTechs and Gau's Rages -- the remaining
 * learned-skill masks, all global save data and all the same chip row.
 * Rage's 256 entries make its "Learn all" the point of the row; picking one
 * of 256 from a plain dropdown is a last resort, not the main path.
 */
function renderGameEditorBlitzes(plugin) {
  renderGlobalChipRow(el.gameEditorBlitzesList, {
    rowLabel: "Sabin's Blitzes",
    testId: 'blitz',
    allItems: plugin.blitzes,
    knownIds: plugin.readBlitzes(state.core).knownIds,
    onLearn: (id) => plugin.learnBlitz(state.core, id),
    onForget: (id) => plugin.forgetBlitz(state.core, id),
    noun: 'Blitz',
    learnAll: true,
  });
}

function renderGameEditorSwdtechs(plugin) {
  renderGlobalChipRow(el.gameEditorSwdtechsList, {
    rowLabel: "Cyan's SwdTechs",
    testId: 'swdtech',
    allItems: plugin.swdtechs,
    knownIds: plugin.readSwdtechs(state.core).knownIds,
    onLearn: (id) => plugin.learnSwdtech(state.core, id),
    onForget: (id) => plugin.forgetSwdtech(state.core, id),
    noun: 'SwdTech',
    learnAll: true,
  });
}

function renderGameEditorRages(plugin) {
  renderGlobalChipRow(el.gameEditorRagesList, {
    rowLabel: "Gau's Rages",
    testId: 'rage',
    allItems: plugin.rages,
    knownIds: plugin.readRages(state.core).knownIds,
    onLearn: (id) => plugin.learnRage(state.core, id),
    onForget: (id) => plugin.forgetRage(state.core, id),
    noun: 'Rage',
    learnAll: true,
  });
}

/**
 * Mog's Dances: global save data like Lore, so it renders regardless of who
 * is in the active party -- same chip row, same bulk "Learn all".
 */
function renderGameEditorDances(plugin) {
  const { knownIds } = plugin.readDances(state.core);
  renderGlobalChipRow(el.gameEditorDancesList, {
    rowLabel: "Mog's Dances",
    testId: 'dance',
    allItems: plugin.dances,
    knownIds,
    onLearn: (id) => plugin.learnDance(state.core, id),
    onForget: (id) => plugin.forgetDance(state.core, id),
    noun: 'Dance',
    learnAll: true,
  });
}

/**
 * Party GP -- global save data, so it renders even when no party member is
 * in the active-party table yet (unlike the per-character stat rows).
 * Writes live WRAM on `change` like every other party-editor field; make a
 * save state afterwards to keep it.
 */
function renderGameEditorGp(plugin) {
  const input = el.gameEditorGpInput;
  input.max = String(plugin.gpMax ?? 9999999);
  // Don't fight the user mid-type: only refresh the displayed value when the
  // field isn't focused, since this re-renders on every panel refresh.
  if (document.activeElement !== input) input.value = String(plugin.readGp(state.core));

  if (input.dataset.wired === 'true') return;
  input.dataset.wired = 'true';
  input.addEventListener('change', () => {
    const max = plugin.gpMax ?? 9999999;
    const clamped = Math.min(max, Math.max(0, Math.round(Number(input.value) || 0)));
    input.value = String(clamped);
    plugin.writeGp(state.core, clamped);
    log(`Set GP to ${clamped.toLocaleString()}.`);
    input.classList.add('applied');
    setTimeout(() => input.classList.remove('applied'), 1200);
  });
}

function renderGameEditorInventory(plugin) {
  el.gameEditorInventoryList.replaceChildren();
  const entries = plugin.readInventory(state.core);

  if (entries.length === 0) {
    el.gameEditorInventoryList.append(makeEmptyItem('No items.'));
  }

  for (const entry of entries) {
    const li = document.createElement('li');
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = entry.name;
    li.append(name);

    const qty = document.createElement('input');
    qty.type = 'number';
    qty.min = '1';
    qty.max = '99';
    qty.value = String(entry.qty);
    qty.className = 'game-editor-inventory-qty';
    qty.setAttribute('data-testid', `game-editor-inventory-qty-${entry.slot}`);
    qty.addEventListener('change', () => {
      const clamped = Math.min(99, Math.max(1, Math.round(Number(qty.value) || 1)));
      qty.value = String(clamped);
      plugin.setInventoryQty(state.core, entry.slot, clamped);
      log(`Set ${entry.name} quantity to ${clamped}.`);
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'danger';
    remove.textContent = 'Remove';
    remove.setAttribute('data-testid', `game-editor-inventory-remove-${entry.slot}`);
    remove.addEventListener('click', () => {
      plugin.removeInventoryItem(state.core, entry.slot);
      log(`Removed ${entry.name} from the inventory.`);
      renderGameEditor();
    });

    li.append(qty, remove);
    el.gameEditorInventoryList.append(li);
  }

  const addRow = document.createElement('li');
  // Already-held items are excluded here -- adding more of one you already
  // have is what the quantity input on its own row is for; this dropdown
  // is for picking up something new.
  const heldItemIds = new Set(entries.map((entry) => entry.itemId));
  const { wrap: selectWrap, select } = createSearchableSelect({
    testId: 'game-editor-inventory-add-select',
    options: plugin.inventoryItems.filter((item) => !heldItemIds.has(item.id)),
    searchValue: state.gameEditorInventorySearch,
    onSearch: (text) => {
      state.gameEditorInventorySearch = text;
    },
  });
  const addQty = document.createElement('input');
  addQty.type = 'number';
  addQty.min = '1';
  addQty.max = '99';
  addQty.value = '1';
  addQty.className = 'game-editor-inventory-qty';
  addQty.setAttribute('data-testid', 'game-editor-inventory-add-qty');
  const add = document.createElement('button');
  add.type = 'button';
  add.className = 'button';
  add.textContent = 'Add';
  add.setAttribute('data-testid', 'game-editor-inventory-add');
  add.addEventListener('click', () => {
    const itemId = Number(select.value);
    const qty = Math.min(99, Math.max(1, Math.round(Number(addQty.value) || 1)));
    const item = plugin.inventoryItems.find((i) => i.id === itemId);
    const slot = plugin.addInventoryItem(state.core, itemId, qty);
    if (slot === null) {
      log('Inventory is completely full -- could not add the item.', true);
      return;
    }
    log(`Added ${qty}x ${item?.label ?? itemId} to the inventory.`);
    renderGameEditor();
  });
  addRow.append(selectWrap, addQty, add);
  el.gameEditorInventoryList.append(addRow);
}

function makeEmptyItem(text) {
  const li = document.createElement('li');
  li.className = 'empty';
  li.textContent = text;
  return li;
}

/* ------------------------------------------------------ service worker -- */

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    appStatus.serviceWorker = 'unsupported';
    return;
  }

  // In dev, Vite serves modules unbundled and a cache-first worker would
  // shadow edits. The worker is only useful for the built app anyway.
  if (import.meta.env?.DEV) {
    appStatus.serviceWorker = 'skipped-in-dev';
    return;
  }

  const register = () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then(async (reg) => {
        appStatus.serviceWorker = 'registered';
        console.log('[snes9x-web] service worker registered', reg.scope);
        await warmCache();
      })
      .catch((err) => {
        appStatus.serviceWorker = `failed: ${err?.message ?? err}`;
        console.warn('[snes9x-web] service worker registration failed', err);
      });
  };

  /**
   * Tell the active worker which same-origin resources this page actually
   * loaded, so it can cache the ones it never saw itself.
   *
   * This matters on the first visit: the document's own hashed bundles are
   * fetched before the worker takes control, so without this they would only
   * land in the cache on the *second* visit -- and a user who goes offline
   * after one visit would get a broken app.
   */
  async function warmCache() {
    const worker = (await navigator.serviceWorker.ready).active;
    if (!worker) return;

    const urls = performance
      .getEntriesByType('resource')
      .map((e) => e.name)
      .filter((name) => {
        try {
          return new URL(name).origin === window.location.origin;
        } catch {
          return false;
        }
      });

    // The document itself is not a "resource" entry, and the wasm binary is
    // fetched by the emcc glue in a way that is not always reported.
    urls.push(window.location.href, '/wasm/snes9x_core.js', '/wasm/snes9x_core.wasm');

    worker.postMessage({ type: 'CACHE_URLS', urls: [...new Set(urls)] });
    console.log(`[snes9x-web] asked the service worker to cache ${urls.length} URLs`);
  }

  // boot() is async and awaits the wasm core, so the window 'load' event has
  // usually already fired by the time we get here -- adding a listener for it
  // would then never run. Check first.
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}

/* ----------------------------------------------------------------------- */

boot().catch((err) => {
  appStatus.error = String(err?.message ?? err);
  log(`Startup failed: ${appStatus.error}`, true);
});
